/**
 * TemplatesPorPaisPage — /admin/country-templates · /admin/dual-cta
 *
 * Dueño de DualCTAVideoLanding (home no-AR):
 *   1. Video al init (Mux playback) — global default + override por país
 *   2. Stripe VIP price_… — cobro Membresía Exclusiva
 *   3. GeoSim — preview edge como visitante del país
 *
 * Save principal = live al instante (test → live + broadcast SPA).
 * “Guardar borrador” = preview sin tocar prod.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { ConfigOwnerCallout } from "@/components/admin/ConfigOwnerCallout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CountryTemplateMobilePreview } from "@/components/admin/CountryTemplateMobilePreview";
import { GeoSimCountryPreviewButton } from "@/components/admin/GeoSimCountryPreviewButton";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { broadcastSiteSettingsUpdated } from "@/lib/siteSettingsSync";
import { broadcastPremiumInviteConfigUpdated } from "@/lib/premiumInvitePublicConfig";
import { MuxVideoPicker } from "@/components/admin/MuxVideoPicker";
import {
  Loader2,
  Save,
  UploadCloud,
  Eye,
  Globe,
  RotateCcw,
  Sparkles,
  Video,
  Layers,
  AlertTriangle,
  CheckCircle2,
  Radio,
  Zap,
  Pencil,
  PlayCircle,
  Plus,
  Trash2,
  CreditCard,
} from "lucide-react";
import {
  DEFAULT_INTERNATIONAL_HOME_TEMPLATE,
  DUAL_CTA_VIDEO_LANDING_TEMPLATE,
  INVITACION_EXCLUSIVA_MUX_TEMPLATE,
  INTERNATIONAL_HOME_TEMPLATE_OPTIONS,
  internationalHomeTemplateSupportsMuxOverride,
  internationalHomeTemplateSupportsVipPricingOverride,
  isDualCtaVideoLandingTemplate,
  isInvitacionExclusivaMuxTemplate,
  isMuxVideoPlayerHomeTemplate,
  isValidMuxPlaybackId,
  isValidStripePriceId,
  normalizeInternationalHomeTemplate,
  parseInternationalHomeMuxByCountry,
  parseInternationalHomeMuxByCountryRich,
  parseMuxCountryConfig,
  getCountryVariantsList,
  emptyVariant,
  parseInternationalHomeTemplateByCountry,
  parseInternationalHomeVipPricingByCountry,
  parseInternationalHomeDualCtaFreeTelegramByCountry,
  pruneInternationalHomeMuxByCountry,
  pruneInternationalHomeVipPricingByCountry,
  pruneInternationalHomeDualCtaFreeTelegramByCountry,
  type InternationalHomeMuxByCountryRaw,
  type MuxCountryVariant,
  type MuxCountryConfig,
} from "@/lib/internationalHomeTemplate";
import {
  FALLBACK_VIP_DISPLAY_DISCOUNT_PCT,
  FALLBACK_VIP_DISPLAY_PRICE_USD,
  FALLBACK_VIP_STRIPE_PRICE_ID,
  FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID,
  resolveInternationalHomeOffer,
} from "@/lib/resolveInternationalHomeOffer";
import {
  countArGeoMuxOverrides,
  parseInternationalHomeMuxByArGeo,
  type InternationalHomeMuxByArGeo,
} from "@/lib/internationalHomeMuxArGeo";
import { AR_CITY_CATALOG, AR_PROVINCES } from "@/config/wishlistGeoCatalog";
import { LATAM_COUNTRY_TEMPLATES } from "@/config/countryTemplates";
import { WORLD_COUNTRIES } from "@/lib/worldCountries";

const USE_GLOBAL = "__global__";

const PRIORITY_COUNTRIES = [
  "AR",
  "US",
  "MX",
  "ES",
  "BR",
  "GB",
  "DE",
  "FR",
  "CO",
  "CL",
] as const;

type ListFilter = "priority" | "overrides" | "mux" | "all";

type CountryPricingDraft = {
  fullPriceUsd: string;
  firstMonthDiscountPct: string;
  stripePriceId: string;
  stripeProductId: string;
  applyDiscount: string; // "true" | "" for draft JSON-friendliness
  promotionCodeId: string;
};

const EMPTY_PRICING_DRAFT = (): CountryPricingDraft => ({
  fullPriceUsd: "",
  firstMonthDiscountPct: "",
  stripePriceId: "",
  stripeProductId: "",
  applyDiscount: "",
  promotionCodeId: "",
});

function isPricingDraftEmpty(d: CountryPricingDraft): boolean {
  return (
    !d.fullPriceUsd.trim() &&
    !d.firstMonthDiscountPct.trim() &&
    !d.stripePriceId.trim() &&
    !d.stripeProductId.trim() &&
    d.applyDiscount !== "true" &&
    !d.promotionCodeId.trim()
  );
}

type FreeTelegramTriState = "inherit" | "on" | "off";

function freeTelegramTriFromBool(v: boolean | null | undefined): FreeTelegramTriState {
  if (v === true) return "on";
  if (v === false) return "off";
  return "inherit";
}

function freeTelegramTriToBool(t: FreeTelegramTriState): boolean | null {
  if (t === "inherit") return null;
  return t === "on";
}

type FormState = {
  globalTpl: string;
  byCountry: Record<string, string>;
  muxByCountry: InternationalHomeMuxByCountryRaw;
  muxByArGeo: InternationalHomeMuxByArGeo;
  pricingByCountry: Record<string, CountryPricingDraft>;
  globalDualCtaFreeTelegram: FreeTelegramTriState;
  freeTelegramByCountry: Record<string, FreeTelegramTriState>;
};

/** true si el país tiene al menos un playback Mux habilitado (1 o N variantes). */
function countryHasActiveMux(
  entry: string | MuxCountryConfig | undefined,
): boolean {
  const cfg = parseMuxCountryConfig(entry);
  return Boolean(
    cfg?.variants.some((v) => v.enabled && isValidMuxPlaybackId(v.playbackId)),
  );
}

function firstMuxPlaybackId(
  entry: string | MuxCountryConfig | undefined,
): string {
  const cfg = parseMuxCountryConfig(entry);
  if (!cfg) return "";
  const en = cfg.variants.find(
    (v) => v.enabled && isValidMuxPlaybackId(v.playbackId),
  );
  return en?.playbackId ?? cfg.variants[0]?.playbackId ?? "";
}

function activeMuxVariantCount(
  entry: string | MuxCountryConfig | undefined,
): number {
  const cfg = parseMuxCountryConfig(entry);
  if (!cfg) return 0;
  return cfg.variants.filter(
    (v) => v.enabled && isValidMuxPlaybackId(v.playbackId),
  ).length;
}

/**
 * Admin draft: keep empty playback rows while editing.
 * Save path uses pruneInternationalHomeMuxByCountry (valid IDs only).
 */
function packMuxCountryDraft(
  variants: MuxCountryVariant[],
): MuxCountryConfig | null {
  if (!variants.length) return null;
  if (variants.every((v) => !String(v.playbackId || "").trim())) return null;
  return {
    variants: variants.map((v, i) => ({
      key: (v.key || `v${i + 1}`).slice(0, 32),
      playbackId: String(v.playbackId || "").trim(),
      label: (v.label || `Variante ${i + 1}`).slice(0, 80),
      weight:
        Number.isFinite(v.weight) && v.weight > 0
          ? Math.min(1000, Math.round(v.weight))
          : 50,
      enabled: v.enabled !== false,
    })),
  };
}

/** Read variants for editor including incomplete draft rows. */
function getCountryVariantsListDraft(
  byCountry: InternationalHomeMuxByCountryRaw,
  code: string,
): MuxCountryVariant[] {
  const entry = byCountry[code.toUpperCase()] ?? byCountry[code];
  if (entry == null) return [];
  if (typeof entry === "string") {
    const pid = entry.trim();
    if (!pid) return [];
    return [
      {
        key: "default",
        playbackId: pid,
        label: "Principal",
        weight: 100,
        enabled: true,
      },
    ];
  }
  if (entry && typeof entry === "object" && Array.isArray(entry.variants)) {
    return entry.variants.map((v, i) => ({
      key: (v.key || `v${i + 1}`).slice(0, 32),
      playbackId: String(v.playbackId || "").trim(),
      label: (v.label || `Variante ${i + 1}`).slice(0, 80),
      weight:
        Number.isFinite(v.weight) && v.weight > 0
          ? Math.min(1000, Math.round(v.weight))
          : 50,
      enabled: v.enabled !== false,
    }));
  }
  return getCountryVariantsList(byCountry, code);
}

const EMPTY_MUX_AR_GEO = (): InternationalHomeMuxByArGeo => ({
  provinces: {},
  cities: {},
});

const TEMPLATE_CARDS: {
  value: string;
  title: string;
  subtitle: string;
  recommended?: boolean;
  videoOnInit?: boolean;
}[] = [
  {
    value: DUAL_CTA_VIDEO_LANDING_TEMPLATE,
    title: "Dual CTA",
    subtitle: "Video al abrir + membresía Stripe + canal gratis",
    recommended: true,
    videoOnInit: true,
  },
  {
    value: INVITACION_EXCLUSIVA_MUX_TEMPLATE,
    title: "Mux nativo",
    subtitle: "Video al abrir + 1 CTA membresía",
    videoOnInit: true,
  },
  {
    value: "BioTemplatePremiumSimple",
    title: "Premium simple",
    subtitle: "Bio primero; video tras clic",
    videoOnInit: false,
  },
  {
    value: "BioTemplatePremium",
    title: "Premium invitación",
    subtitle: "Bio + plan; video tras clic VIP",
    videoOnInit: false,
  },
  {
    value: "BioLinkTemplate",
    title: "Bio link",
    subtitle: "Links primero; video tras clic",
    videoOnInit: false,
  },
];

const COUNTRIES: { code: string; name: string }[] = (() => {
  try {
    const names = new Map<string, string>();
    for (const c of WORLD_COUNTRIES) {
      const code = c.code.trim().toUpperCase();
      if (code) names.set(code, c.name);
    }
    for (const c of LATAM_COUNTRY_TEMPLATES ?? []) {
      const code = c?.countryCode?.trim?.().toUpperCase?.();
      if (!code) continue;
      names.set(code, c.countryName || names.get(code) || code);
    }
    return Array.from(names, ([code, name]) => ({ code, name })).sort((a, b) =>
      a.name.localeCompare(b.name, "es"),
    );
  } catch {
    return [];
  }
})();

const COUNTRY_BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c.name]));

function flag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return "🏳️";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + code.toUpperCase().charCodeAt(0) - 65,
    A + code.toUpperCase().charCodeAt(1) - 65,
  );
}

function templateBadgeVariant(
  tpl: string,
): "default" | "secondary" | "outline" | "destructive" {
  if (isInvitacionExclusivaMuxTemplate(tpl)) return "default";
  if (isDualCtaVideoLandingTemplate(tpl)) return "secondary";
  if (tpl.includes("Premium")) return "outline";
  return "outline";
}

function templateShortLabel(tpl: string): string {
  const found = INTERNATIONAL_HOME_TEMPLATE_OPTIONS.find((o) => o.value === tpl);
  if (!found) return tpl;
  return found.label.split(/[—(]/)[0].trim();
}

function parseFormFromSettings(src: Record<string, unknown>): FormState {
  const pricingParsed = parseInternationalHomeVipPricingByCountry(
    src.internationalHomeVipPricingByCountry,
  );
  const freeTelegramParsed = parseInternationalHomeDualCtaFreeTelegramByCountry(
    src.internationalHomeDualCtaFreeTelegramByCountry,
  );
  return {
    globalTpl: normalizeInternationalHomeTemplate(
      typeof src.internationalHomeTemplate === "string"
        ? src.internationalHomeTemplate
        : undefined,
    ),
    byCountry: parseInternationalHomeTemplateByCountry(
      src.internationalHomeTemplateByCountry,
    ),
    muxByCountry: parseInternationalHomeMuxByCountryRich(
      src.internationalHomeMuxByCountry,
    ),
    muxByArGeo: parseInternationalHomeMuxByArGeo(
      src.internationalHomeMuxByArGeo,
    ),
    pricingByCountry: Object.fromEntries(
      Object.entries(pricingParsed).map(([code, entry]) => [
        code,
        {
          fullPriceUsd: String(entry.fullPriceUsd),
          firstMonthDiscountPct:
            entry.firstMonthDiscountPct != null
              ? String(entry.firstMonthDiscountPct)
              : "",
          stripePriceId: entry.stripePriceId ?? "",
          stripeProductId: entry.stripeProductId ?? "",
          applyDiscount: entry.applyDiscount ? "true" : "",
          promotionCodeId: entry.promotionCodeId ?? "",
        },
      ]),
    ),
    globalDualCtaFreeTelegram: freeTelegramTriFromBool(
      src.internationalHomeDualCtaFreeTelegramEnabled as boolean | null | undefined,
    ),
    freeTelegramByCountry: Object.fromEntries(
      Object.entries(freeTelegramParsed).map(([code, enabled]) => [
        code,
        freeTelegramTriFromBool(enabled),
      ]),
    ),
  };
}

function draftFreeTelegramToNormalized(
  draft: Record<string, FreeTelegramTriState>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [code, tri] of Object.entries(draft)) {
    if (tri === "inherit") continue;
    out[code] = tri === "on";
  }
  return out;
}

function draftPricingToNormalized(
  draft: Record<string, CountryPricingDraft>,
): Record<
  string,
  {
    fullPriceUsd: number;
    firstMonthDiscountPct?: number;
    stripePriceId?: string;
    stripeProductId?: string;
    applyDiscount?: boolean;
    promotionCodeId?: string;
  }
> {
  const out: Record<
    string,
    {
      fullPriceUsd: number;
      firstMonthDiscountPct?: number;
      stripePriceId?: string;
      stripeProductId?: string;
      applyDiscount?: boolean;
      promotionCodeId?: string;
    }
  > = {};
  for (const [code, raw] of Object.entries(draft)) {
    const full = parseFloat(raw.fullPriceUsd);
    if (!Number.isFinite(full) || full <= 0) continue;
    const discRaw = raw.firstMonthDiscountPct.trim();
    const disc = discRaw ? parseFloat(discRaw) : 0;
    const stripePriceId = raw.stripePriceId.trim();
    // Always this brand’s single VIP product (never multi-brand / per-country products)
    const stripeProductId = "prod_UsCLwRCliyEnz0";
    // Promo only when checkbox is on — never invent from % alone
    const applyDiscount = raw.applyDiscount === "true";
    const promotionCodeId = raw.promotionCodeId.trim();
    const discPct =
      Number.isFinite(disc) && disc > 0
        ? Math.min(90, Math.max(1, Math.round(disc)))
        : 0;
    out[code] = {
      fullPriceUsd: Math.round(full * 100) / 100,
      // UI copy % only when applyDiscount is on (unchecked clears field in setPricing)
      ...(applyDiscount && discPct > 0 ? { firstMonthDiscountPct: discPct } : {}),
      ...(stripePriceId ? { stripePriceId } : {}),
      stripeProductId,
      ...(applyDiscount && promotionCodeId
        ? { applyDiscount: true, promotionCodeId }
        : {}),
    };
  }
  return out;
}

function snapshotForm(form: FormState): string {
  return JSON.stringify(form);
}

function effectiveTemplateFor(
  code: string,
  globalTpl: string,
  byCountry: Record<string, string>,
): string {
  return byCountry[code] ?? globalTpl;
}

type CountryRowProps = {
  code: string;
  name: string;
  globalTpl: string;
  byCountry: Record<string, string>;
  muxByCountry: InternationalHomeMuxByCountryRaw;
  pricingByCountry: Record<string, CountryPricingDraft>;
  freeTelegramByCountry: Record<string, FreeTelegramTriState>;
  onSetCountry: (code: string, value: string) => void;
  onSetMuxVariants: (code: string, variants: MuxCountryVariant[]) => void;
  onSetPricing: (code: string, field: keyof CountryPricingDraft, value: string) => void;
  /** Crea Product/Price (+ promo) en Stripe y rellena IDs del país. */
  onCreateStripeVip?: (code: string) => Promise<void>;
  creatingStripeFor?: string | null;
  onSetFreeTelegram: (code: string, value: FreeTelegramTriState) => void;
  onApplyMux: (code: string) => void;
  onApplyDualCta: (code: string) => void;
  onEditMobile?: (code: string) => void;
  editingCountry?: string | null;
  compact?: boolean;
};

/** Editor multi-variante Mux por país (A/B con label + weight). */
function CountryMuxVariantsEditor({
  code,
  variants,
  onChange,
  compact = false,
}: {
  code: string;
  variants: MuxCountryVariant[];
  onChange: (next: MuxCountryVariant[]) => void;
  compact?: boolean;
}) {
  const list =
    variants.length > 0
      ? variants
      : [{ ...emptyVariant(0), playbackId: "", enabled: true }];

  const updateAt = (index: number, patch: Partial<MuxCountryVariant>) => {
    const next = list.map((v, i) => (i === index ? { ...v, ...patch } : v));
    onChange(next);
  };

  const removeAt = (index: number) => {
    if (list.length <= 1) {
      onChange([]);
      return;
    }
    onChange(list.filter((_, i) => i !== index));
  };

  const addVariant = () => {
    onChange([...list, emptyVariant(list.length)]);
  };

  const isAb = list.filter((v) => isValidMuxPlaybackId(v.playbackId)).length > 1;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-[11px] font-medium text-muted-foreground">
          Video Mux {code}
          {isAb && (
            <Badge
              variant="outline"
              className="ml-1.5 border-violet-500/40 text-[10px] text-violet-600"
            >
              A/B · {list.filter((v) => v.enabled).length} variantes
            </Badge>
          )}
        </label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1 text-xs"
          onClick={addVariant}
        >
          <Plus className="h-3.5 w-3.5" />
          Variante
        </Button>
      </div>

      {list.map((v, index) => {
        const valid = isValidMuxPlaybackId(v.playbackId);
        return (
          <div
            key={`${v.key}-${index}`}
            className={cn(
              "space-y-2 rounded-lg border bg-background/60 p-2.5",
              valid && v.enabled && "border-amber-500/30",
              !v.enabled && "opacity-60",
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={v.label}
                onChange={(e) => updateAt(index, { label: e.target.value })}
                placeholder="Nombre (ej. Hero v2)"
                className="h-8 min-w-[8rem] flex-1 text-sm"
              />
              <Input
                value={v.key}
                onChange={(e) =>
                  updateAt(index, {
                    key: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_-]+/g, "-")
                      .slice(0, 32),
                  })
                }
                placeholder="key"
                className="h-8 w-24 font-mono text-xs"
                title="Clave analytics (PostHog: mux_variant_key)"
              />
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">peso</span>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={v.weight}
                  onChange={(e) =>
                    updateAt(index, {
                      weight: Math.max(1, Math.min(1000, Number(e.target.value) || 50)),
                    })
                  }
                  className="h-8 w-16 font-mono text-xs"
                  title="Peso relativo de tráfico (mayor = más visitas)"
                />
              </div>
              <label className="flex cursor-pointer items-center gap-1 text-[10px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={v.enabled}
                  onChange={(e) => updateAt(index, { enabled: e.target.checked })}
                  className="h-3.5 w-3.5 rounded"
                />
                on
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeAt(index)}
                title="Quitar variante"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <MuxVideoPicker
              value={v.playbackId}
              onChange={(id) => updateAt(index, { playbackId: id })}
              placeholder={
                compact
                  ? `Playback ID variante ${v.label || v.key}`
                  : `Playback ID para ${code} · ${v.label || v.key}`
              }
            />
            {valid && (
              <p className="font-mono text-[10px] text-muted-foreground">
                {v.playbackId}
                {isAb && (
                  <span className="ml-2 text-violet-600/90">
                    · track: mux_variant_key={v.key}
                  </span>
                )}
              </p>
            )}
          </div>
        );
      })}

      {isAb && (
        <p className="text-[10px] leading-snug text-muted-foreground">
          A/B sticky: el visitante ve una variante según el peso y se queda con ella.
          En PostHog filtrá por <code className="text-[9px]">mux_variant_key</code> /{" "}
          <code className="text-[9px]">mux_variant_label</code> para ver cuál convierte mejor
          (views, CTA, checkout).
        </p>
      )}
      {!isAb && (
        <p className="text-[10px] leading-snug text-muted-foreground">
          Agregá otra variante para comparar videos en el mismo país.{" "}
          <strong className="font-medium text-foreground/80">Guardar (live)</strong> aplica sin
          deploy.
        </p>
      )}
    </div>
  );
}

function CountryEditorPanel({
  code,
  globalTpl,
  byCountry,
  muxByCountry,
  pricingByCountry,
  freeTelegramByCountry,
  onSetCountry,
  onSetMuxVariants,
  onSetPricing,
  onCreateStripeVip,
  creatingStripeFor = null,
  onSetFreeTelegram,
  onApplyMux,
  onApplyDualCta,
  stacked = false,
}: Omit<CountryRowProps, "name" | "onEditMobile" | "compact"> & { stacked?: boolean }) {
  const current = byCountry[code] ?? USE_GLOBAL;
  const effectiveTpl = effectiveTemplateFor(code, globalTpl, byCountry);
  const showsMuxInput = internationalHomeTemplateSupportsMuxOverride(effectiveTpl);
  const showsVipPricing = internationalHomeTemplateSupportsVipPricingOverride(effectiveTpl);
  const muxVariants = getCountryVariantsListDraft(muxByCountry, code);
  const muxId = firstMuxPlaybackId(muxByCountry[code]);
  const pricing = pricingByCountry[code] ?? EMPTY_PRICING_DRAFT();
  const freeTelegramTri = freeTelegramByCountry[code] ?? "inherit";
  const globalShort = templateShortLabel(globalTpl);
  const normalizedPricing = draftPricingToNormalized(pricingByCountry);
  const flatMux = parseInternationalHomeMuxByCountry(muxByCountry);
  const offerPreview = resolveInternationalHomeOffer({
    country: code,
    muxByCountry: flatMux,
    vipPricingByCountry: normalizedPricing,
    playbackIdOverride: muxId || null,
  });

  return (
    <div className={cn("flex w-full min-w-0 flex-col gap-3", stacked ? "" : "lg:max-w-md")}>
      <div className={cn("flex gap-2", stacked ? "flex-col" : "flex-wrap items-center")}>
        <Select value={current} onValueChange={(v) => onSetCountry(code, v)}>
          <SelectTrigger className={cn("h-11", stacked ? "w-full" : "h-9 min-w-[200px] flex-1")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[min(360px,70dvh)]">
            <SelectItem value={USE_GLOBAL}>Global ({globalShort})</SelectItem>
            {INTERNATIONAL_HOME_TEMPLATE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className={cn("flex gap-2", stacked && "grid grid-cols-3")}>
          {!isInvitacionExclusivaMuxTemplate(effectiveTpl) && (
            <Button
              type="button"
              variant="default"
              size="sm"
              className={cn("h-11 shrink-0", stacked && "col-span-1")}
              onClick={() => onApplyMux(code)}
            >
              Mux
            </Button>
          )}
          {!isDualCtaVideoLandingTemplate(effectiveTpl) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn("h-11 shrink-0", stacked && "col-span-1")}
              onClick={() => onApplyDualCta(code)}
            >
              Dual
            </Button>
          )}
          <GeoSimCountryPreviewButton
            country={code}
            source="country_templates_row"
            className={cn("h-11 shrink-0", stacked ? "col-span-1" : "px-3")}
            label={
              stacked ? (
                "Ver"
              ) : (
                <span className="sr-only">GeoSim {code}</span>
              )
            }
            showIcon
            title={`GeoSim edge · visitante ${code} (template + CTA + pago reales)`}
          />
        </div>
      </div>

      {showsMuxInput && (
        <div className="space-y-1.5">
          <CountryMuxVariantsEditor
            code={code}
            variants={muxVariants}
            onChange={(next) => onSetMuxVariants(code, next)}
            compact={stacked}
          />
          <p className="text-[10px] leading-snug text-muted-foreground">
            DualCTA / InvitacionExclusivaMux. Vacío = fallback en{" "}
            <Link to="/admin/premium-invite-config" className="underline underline-offset-2">
              Premium Invite
            </Link>
            .
          </p>
        </div>
      )}

      {showsVipPricing && (
        <div className="space-y-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3">
          <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
            Membresía Stripe VIP (Price + promo opcional)
          </p>
          <div className="space-y-1">
            <label className="text-[10px] text-muted-foreground">
              Stripe Price ID <span className="text-amber-600/80">(price_… — requerido para cobro)</span>
            </label>
            <Input
              type="text"
              placeholder="price_1TsRwOH8xCanmtKXBEW8SAea"
              value={pricing.stripePriceId}
              onChange={(e) => onSetPricing(code, "stripePriceId", e.target.value)}
              className="h-9 font-mono text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] text-muted-foreground">
              Stripe Product ID{" "}
              <span className="opacity-70">(solo catálogo de esta brand)</span>
            </label>
            <Input
              type="text"
              placeholder="prod_UsCLwRCliyEnz0"
              value={pricing.stripeProductId || "prod_UsCLwRCliyEnz0"}
              readOnly
              title="Un solo product VIP para anaafernandez — se gestiona en Stripe / env STRIPE_VIP_PRODUCT_ID"
              className="h-9 font-mono text-sm opacity-80"
            />
            <p className="text-[9px] text-muted-foreground">
              Catálogo único de esta brand (no multi-product por país ni otros brands).
            </p>
          </div>
          <div className={cn("grid gap-2", stacked ? "grid-cols-1" : "grid-cols-2")}>
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">
                Precio display USD (mensual)
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="29.99"
                value={pricing.fullPriceUsd}
                onChange={(e) => onSetPricing(code, "fullPriceUsd", e.target.value)}
                className="h-9 font-mono text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">
                % off 1.er mes <span className="opacity-70">(solo copy UI)</span>
              </label>
              <Input
                type="number"
                min="0"
                max="90"
                step="1"
                placeholder="70"
                value={pricing.firstMonthDiscountPct}
                onChange={(e) => onSetPricing(code, "firstMonthDiscountPct", e.target.value)}
                className="h-9 font-mono text-sm"
                disabled={pricing.applyDiscount !== "true"}
              />
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-[11px] text-foreground/90">
            <input
              type="checkbox"
              checked={pricing.applyDiscount === "true"}
              onChange={(e) =>
                onSetPricing(code, "applyDiscount", e.target.checked ? "true" : "")
              }
              className="h-3.5 w-3.5 rounded border-white/30"
            />
            Aplicar descuento 1.er mes (Stripe Promotion Code)
          </label>
          {pricing.applyDiscount === "true" && (
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">
                Promotion Code ID <span className="text-amber-600/80">(promo_…)</span>
              </label>
              <Input
                type="text"
                placeholder="promo_1TzyMyH8xCanmtKXKsOmWgAp"
                value={pricing.promotionCodeId}
                onChange={(e) => onSetPricing(code, "promotionCodeId", e.target.value)}
                className="h-9 font-mono text-sm"
              />
            </div>
          )}
          {onCreateStripeVip && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-9 w-full text-xs"
              disabled={
                creatingStripeFor === code ||
                !pricing.fullPriceUsd.trim() ||
                !(parseFloat(pricing.fullPriceUsd) > 0)
              }
              onClick={() => void onCreateStripeVip(code)}
            >
              {creatingStripeFor === code ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Creando en Stripe…
                </>
              ) : (
                <>
                  <CreditCard className="mr-1.5 h-3.5 w-3.5" />
                  Crear Price
                  {pricing.applyDiscount === "true" ? " + cupón" : ""} y publicar live
                </>
              )}
            </Button>
          )}
          {!isValidStripePriceId(pricing.stripePriceId) &&
            pricing.fullPriceUsd.trim() &&
            parseFloat(pricing.fullPriceUsd) > 0 && (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-800 dark:text-amber-200">
                <strong>Gap:</strong> hay precio display sin <code>price_…</code>. Usá el botón
                de arriba (crea Stripe + publica live) o <strong>Guardar (live)</strong>{" "}
                (auto-provisiona). Sin eso el cobro usa catálogo/fallback, no este monto.
              </p>
            )}
          <p className="text-[10px] leading-snug text-muted-foreground">
            <strong className="text-foreground/80">Impacto real:</strong> el checkout cobra el{" "}
            <code className="text-[9px]">price_…</code> (mensual). Cupón ={" "}
            <code className="text-[9px]">promo_…</code> duration <em>once</em>. El botón de
            arriba publica live al instante (front DualCTA + Stripe allowlist).
          </p>
          <div className="rounded-md border border-white/10 bg-black/20 px-2.5 py-2 text-[10px] text-muted-foreground space-y-0.5">
            <div>
              <span className="font-medium text-foreground">Vista previa:</span>{" "}
              Mux <span className="font-mono text-amber-600/90">{offerPreview.playbackSource}</span>
              {" · "}
              ${offerPreview.pricing.fullPriceUsd.toFixed(2)}
              {offerPreview.pricing.discountPct > 0
                ? ` → $${(offerPreview.pricing.amountCents / 100).toFixed(2)} (−${offerPreview.pricing.discountPct}%)`
                : ""}
              {" · "}
              fuente <span className="font-mono">{offerPreview.pricing.priceSource}</span>
            </div>
            <div>
              Cobra{" "}
              <span className="font-mono text-amber-600/90">
                {offerPreview.pricing.stripePriceId}
              </span>
              {offerPreview.pricing.promotionCodeId
                ? <>
                    {" + "}
                    <span className="font-mono text-amber-600/90">
                      {offerPreview.pricing.promotionCodeId}
                    </span>
                  </>
                : " · sin promo auto"}
              {offerPreview.pricing.usingFallbackPrice ? " (fallback)" : ""}
            </div>
          </div>
        </div>
      )}

      {isDualCtaVideoLandingTemplate(effectiveTpl) && (
        <div className="space-y-1.5">
          <label className="text-[11px] font-medium text-muted-foreground">
            Canal gratis Telegram
          </label>
          <Select
            value={freeTelegramTri}
            onValueChange={(v) => onSetFreeTelegram(code, v as FreeTelegramTriState)}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inherit">Heredar (global → premium-invite)</SelectItem>
              <SelectItem value="on">Mostrar</SelectItem>
              <SelectItem value="off">Ocultar</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function CountryTemplateRow({
  code,
  name,
  globalTpl,
  byCountry,
  muxByCountry,
  pricingByCountry,
  freeTelegramByCountry,
  onSetCountry,
  onSetMuxVariants,
  onSetPricing,
  onCreateStripeVip,
  creatingStripeFor = null,
  onSetFreeTelegram,
  onApplyMux,
  onApplyDualCta,
  onEditMobile,
  editingCountry = null,
  compact = false,
}: CountryRowProps) {
  const current = byCountry[code] ?? USE_GLOBAL;
  const hasOverride = current !== USE_GLOBAL;
  const effectiveTpl = effectiveTemplateFor(code, globalTpl, byCountry);
  const muxId = firstMuxPlaybackId(muxByCountry[code]);
  const muxVariantN = activeMuxVariantCount(muxByCountry[code]);
  const muxValid = !muxId || isValidMuxPlaybackId(muxId);
  const pricing = pricingByCountry[code];
  const hasPricing =
    internationalHomeTemplateSupportsVipPricingOverride(effectiveTpl) &&
    Boolean(pricing?.fullPriceUsd.trim() || pricing?.stripePriceId.trim());
  const freeTelegramTri = freeTelegramByCountry[code];
  const freeTelegramHidden =
    isDualCtaVideoLandingTemplate(effectiveTpl) && freeTelegramTri === "off";
  const globalShort = templateShortLabel(globalTpl);
  const videoOnInit = isMuxVideoPlayerHomeTemplate(effectiveTpl);

  return (
    <div
      id={`country-row-${code}`}
      className={cn(
        "rounded-xl border bg-card/40 p-3 transition-colors sm:p-4",
        hasOverride && "border-amber-500/30 bg-amber-500/[0.04]",
        muxId && muxValid && "ring-1 ring-amber-500/10",
        editingCountry === code && "ring-2 ring-primary/40",
      )}
    >
      {/* Mobile: tarjeta táctil compacta */}
      <div className="flex items-center gap-3 md:hidden">
        <span className="text-2xl leading-none">{flag(code)}</span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-medium">{name}</span>
            <span className="font-mono text-[10px] text-muted-foreground">{code}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            <Badge variant={templateBadgeVariant(effectiveTpl)} className="text-[10px]">
              {hasOverride ? templateShortLabel(current) : `Global · ${globalShort}`}
            </Badge>
            {videoOnInit && (
              <Badge variant="outline" className="gap-0.5 text-[10px] text-emerald-700 dark:text-emerald-400">
                <PlayCircle className="h-3 w-3" />
                Video al abrir
              </Badge>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 shrink-0 px-3"
          onClick={() => onEditMobile?.(code)}
        >
          <Pencil className="mr-1 h-3.5 w-3.5" />
          Editar
        </Button>
      </div>

      {/* Desktop: fila completa */}
      <div className="hidden flex-col gap-3 md:flex lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="text-2xl leading-none">{flag(code)}</span>
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{name}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{code}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={templateBadgeVariant(effectiveTpl)} className="text-[10px]">
                {hasOverride ? templateShortLabel(current) : `Global · ${globalShort}`}
              </Badge>
              {videoOnInit && (
                <Badge variant="outline" className="gap-0.5 text-[10px] text-emerald-700 dark:text-emerald-400">
                  <PlayCircle className="h-3 w-3" />
                  Video al abrir
                </Badge>
              )}
              {muxId && muxValid && (
                <Badge variant="outline" className="text-[10px]">
                  {muxVariantN > 1 ? `Mux A/B · ${muxVariantN}` : "Mux propio"}
                </Badge>
              )}
              {muxId && !muxValid && (
                <Badge variant="destructive" className="text-[10px]">
                  Mux inválido
                </Badge>
              )}
              {hasPricing && (
                <Badge variant="outline" className="text-[10px] text-amber-700 dark:text-amber-400">
                  {pricing!.stripePriceId.trim()
                    ? `${pricing!.stripePriceId.slice(0, 14)}…`
                    : `$${pricing!.fullPriceUsd}`}
                  {pricing!.applyDiscount === "true" && pricing!.firstMonthDiscountPct.trim()
                    ? ` · −${pricing!.firstMonthDiscountPct}%`
                    : ""}
                </Badge>
              )}
              {freeTelegramHidden && (
                <Badge variant="outline" className="text-[10px] text-muted-foreground">
                  Sin TG gratis
                </Badge>
              )}
            </div>
            {!compact && (
              <p className="text-[11px] text-muted-foreground">
                En vivo verán{" "}
                <span className="font-medium text-foreground">
                  {templateShortLabel(effectiveTpl)}
                </span>
                {videoOnInit ? " con video Mux al cargar." : "."}
              </p>
            )}
          </div>
        </div>

        <CountryEditorPanel
          code={code}
          globalTpl={globalTpl}
          byCountry={byCountry}
          muxByCountry={muxByCountry}
          pricingByCountry={pricingByCountry}
          freeTelegramByCountry={freeTelegramByCountry}
          onSetCountry={onSetCountry}
          onSetMuxVariants={onSetMuxVariants}
          onSetPricing={onSetPricing}
          onCreateStripeVip={onCreateStripeVip}
          creatingStripeFor={creatingStripeFor}
          onSetFreeTelegram={onSetFreeTelegram}
          onApplyMux={onApplyMux}
          onApplyDualCta={onApplyDualCta}
        />
      </div>
    </div>
  );
}

export default function TemplatesPorPaisPage() {
  const { toast } = useToast();
  const { getAuthHeaders } = useAdminAuth();
  const isMobile = useIsMobile();

  const [full, setFull] = useState<Record<string, unknown>>({});
  const [globalTpl, setGlobalTpl] = useState<string>(DEFAULT_INTERNATIONAL_HOME_TEMPLATE);
  const [byCountry, setByCountry] = useState<Record<string, string>>({});
  const [muxByCountry, setMuxByCountry] =
    useState<InternationalHomeMuxByCountryRaw>({});
  const [creatingStripeFor, setCreatingStripeFor] = useState<string | null>(null);
  const [muxByArGeo, setMuxByArGeo] = useState<InternationalHomeMuxByArGeo>(
    EMPTY_MUX_AR_GEO,
  );
  const [arGeoTab, setArGeoTab] = useState<"provinces" | "cities">("provinces");
  const [arCityFilter, setArCityFilter] = useState("");
  const [pricingByCountry, setPricingByCountry] = useState<
    Record<string, CountryPricingDraft>
  >({});
  const [globalDualCtaFreeTelegram, setGlobalDualCtaFreeTelegram] =
    useState<FreeTelegramTriState>("inherit");
  const [freeTelegramByCountry, setFreeTelegramByCountry] = useState<
    Record<string, FreeTelegramTriState>
  >({});

  const savedRef = useRef("");
  const liveRef = useRef("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [filter, setFilter] = useState("");
  const [listTab, setListTab] = useState<ListFilter>("all");
  const [allVisibleLimit, setAllVisibleLimit] = useState(60);
  const [previewCountry, setPreviewCountry] = useState("US");
  const [editingCountry, setEditingCountry] = useState<string | null>(null);
  const [quickPickCountry, setQuickPickCountry] = useState("");
  const [globalPricingDraft, setGlobalPricingDraft] = useState<CountryPricingDraft>(
    () => ({
      ...EMPTY_PRICING_DRAFT(),
      fullPriceUsd: String(FALLBACK_VIP_DISPLAY_PRICE_USD),
      firstMonthDiscountPct: String(FALLBACK_VIP_DISPLAY_DISCOUNT_PCT),
      stripePriceId: FALLBACK_VIP_STRIPE_PRICE_ID,
      stripeProductId: "prod_UsCLwRCliyEnz0",
      applyDiscount: "true",
      promotionCodeId: FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID,
    }),
  );

  // Video global por defecto (Mux) — reutiliza el campo ya gestionable en
  // /admin/premium-invite-config (`simple_mux_overlay_playback_id`), que el
  // resolver consume como fuente `runtime` (arriba de env/brand_default). Se
  // gestiona acá para tener plantilla + video en la misma página. Países con
  // "Mux propio" lo overridean.
  const [globalDefaultMux, setGlobalDefaultMux] = useState("");
  const globalDefaultMuxSavedRef = useRef("");
  const [savingDefaultMux, setSavingDefaultMux] = useState(false);
  const globalDefaultMuxDirty = globalDefaultMux.trim() !== globalDefaultMuxSavedRef.current;
  const globalDefaultMuxValid =
    !globalDefaultMux.trim() || isValidMuxPlaybackId(globalDefaultMux.trim());

  const loadGlobalDefaultMux = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/premium-invite-config", { headers });
      if (!res.ok) return;
      const data = (await res.json().catch(() => null)) as
        | { config?: Record<string, unknown> }
        | null;
      const pid = data?.config?.simple_mux_overlay_playback_id;
      const val = typeof pid === "string" ? pid.trim() : "";
      setGlobalDefaultMux(val);
      globalDefaultMuxSavedRef.current = val;
    } catch {
      /* silencioso: el video global es opcional */
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    void loadGlobalDefaultMux();
  }, [loadGlobalDefaultMux]);

  /**
   * Guarda el video DualCTA por defecto (simple_mux_overlay_playback_id).
   * Es la fuente `runtime` del resolver cuando el país no tiene mux_by_country.
   * DualCTA siempre autoplay al init — este ID es el que se reproduce.
   */
  const saveGlobalDefaultMux = async (opts?: { silent?: boolean }): Promise<boolean> => {
    const trimmed = globalDefaultMux.trim();
    if (trimmed && !isValidMuxPlaybackId(trimmed)) {
      toast({
        title: "Playback ID inválido",
        description: "8–128 caracteres [A-Za-z0-9_-]. Corregí antes de guardar.",
        variant: "destructive",
      });
      return false;
    }
    try {
      setSavingDefaultMux(true);
      const headers = await getAuthHeaders();
      // Merge parcial + enable overlay para que DualCTA consuma el ID al init.
      const res = await fetch("/api/admin/premium-invite-config", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          simple_mux_overlay_playback_id: trimmed || null,
          ...(trimmed ? { simple_mux_overlay_enabled: true } : {}),
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error((e as { error?: string }).error || `HTTP ${res.status}`);
      }
      globalDefaultMuxSavedRef.current = trimmed;
      setGlobalDefaultMux(trimmed);
      broadcastPremiumInviteConfigUpdated();
      if (!opts?.silent) {
        toast({
          title: "Video DualCTA al init · live",
          description: trimmed
            ? "Países sin override de Mux reproducen este video al abrir. Sin deploy."
            : "Video global vaciado · fallback env de marca.",
        });
      }
      return true;
    } catch (err) {
      toast({
        title: "Error al guardar video DualCTA",
        description: err instanceof Error ? err.message : "No se pudo guardar",
        variant: "destructive",
      });
      return false;
    } finally {
      setSavingDefaultMux(false);
    }
  };

  /**
   * Un click: fija video al init en global + overrides prioritarios (US, CL, MX…)
   * + DualCTA template + publish live. Cierra el gap “mux_by_country vacío”.
   */
  const applyDefaultMuxToPriorityLive = async () => {
    const trimmed = globalDefaultMux.trim();
    if (!trimmed || !isValidMuxPlaybackId(trimmed)) {
      toast({
        title: "Elegí un playback Mux válido",
        description: "Seleccioná el video arriba y volvé a intentar.",
        variant: "destructive",
      });
      return;
    }
    // DualCTA global + prioritarios intl
    setGlobalTpl(DUAL_CTA_VIDEO_LANDING_TEMPLATE);
    setByCountry((prev) => {
      const next = { ...prev };
      for (const code of PRIORITY_COUNTRIES) {
        if (code === "AR") continue;
        next[code] = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
      }
      return next;
    });
    setMuxByCountry((prev) => {
      const next = { ...prev };
      for (const code of PRIORITY_COUNTRIES) {
        if (code === "AR") continue;
        next[code] = trimmed;
      }
      return next;
    });
    // Dejar que React aplique state antes de publish — next tick
    await new Promise((r) => setTimeout(r, 0));
    const okGlobal = await saveGlobalDefaultMux({ silent: true });
    if (!okGlobal) return;
    // Publish site-settings with the new mux map (buildPayload lee state actual)
    // Nota: setState es async — re-aplicar en payload directo:
    try {
      setPublishing(true);
      const headers = await getAuthHeaders();
      const normalizedGlobal = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
      const normalizedByCountry: Record<string, string> = {};
      for (const code of PRIORITY_COUNTRIES) {
        if (code === "AR") continue;
        normalizedByCountry[code] = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
      }
      // merge existing byCountry overrides for non-priority
      for (const [code, tpl] of Object.entries(byCountry)) {
        if (!normalizedByCountry[code]) {
          normalizedByCountry[code] = normalizeInternationalHomeTemplate(tpl);
        }
      }
      const nextMux: InternationalHomeMuxByCountryRaw = { ...muxByCountry };
      for (const code of PRIORITY_COUNTRIES) {
        if (code === "AR") continue;
        nextMux[code] = trimmed;
      }
      const payload = {
        variant: "test" as const,
        ...full,
        internationalHomeTemplate: normalizedGlobal,
        internationalHomeTemplateByCountry: normalizedByCountry,
        internationalHomeMuxByCountry: pruneInternationalHomeMuxByCountry(
          nextMux,
          normalizedGlobal,
          normalizedByCountry,
        ),
        internationalHomeMuxByArGeo: parseInternationalHomeMuxByArGeo(muxByArGeo),
        internationalHomeVipPricingByCountry: pruneInternationalHomeVipPricingByCountry(
          draftPricingToNormalized(pricingByCountry),
          normalizedGlobal,
          normalizedByCountry,
        ),
        internationalHomeDualCtaFreeTelegramEnabled: freeTelegramTriToBool(
          globalDualCtaFreeTelegram,
        ),
        internationalHomeDualCtaFreeTelegramByCountry:
          pruneInternationalHomeDualCtaFreeTelegramByCountry(
            draftFreeTelegramToNormalized(freeTelegramByCountry),
            normalizedGlobal,
            normalizedByCountry,
          ),
      };
      const res = await fetch("/api/admin/site-settings", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error((e as { error?: string }).error || "save failed");
      }
      const pub = await fetch("/api/admin/site-settings", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "publish" }),
      });
      if (!pub.ok) throw new Error("publish failed");
      broadcastSiteSettingsUpdated();
      toast({
        title: "DualCTA live · video al init",
        description: `Playback aplicado a prioritarios (sin AR) + global. Visitantes intl ven el video al abrir y CTA Stripe.`,
      });
      void load();
      void loadGlobalDefaultMux();
    } catch (err) {
      toast({
        title: "Error al publicar DualCTA",
        description: err instanceof Error ? err.message : "No se pudo publicar",
        variant: "destructive",
      });
    } finally {
      setPublishing(false);
    }
  };

  const currentForm = useMemo<FormState>(
    () => ({
      globalTpl,
      byCountry,
      muxByCountry,
      muxByArGeo,
      pricingByCountry,
      globalDualCtaFreeTelegram,
      freeTelegramByCountry,
    }),
    [
      globalTpl,
      byCountry,
      muxByCountry,
      muxByArGeo,
      pricingByCountry,
      globalDualCtaFreeTelegram,
      freeTelegramByCountry,
    ],
  );

  const hasUnsavedChanges =
    savedRef.current !== "" && snapshotForm(currentForm) !== savedRef.current;

  const hasUnpublishedOnServer =
    liveRef.current !== "" &&
    savedRef.current !== "" &&
    liveRef.current !== savedRef.current;

  const invalidMuxEntries = useMemo(() => {
    const country = Object.entries(muxByCountry).filter(
      ([, id]) => id.trim().length > 0 && !isValidMuxPlaybackId(id),
    );
    const prov = Object.entries(muxByArGeo.provinces)
      .filter(([, id]) => id.trim().length > 0 && !isValidMuxPlaybackId(id))
      .map(([k, id]) => [`AR-prov:${k}`, id] as [string, string]);
    const cities = Object.entries(muxByArGeo.cities)
      .filter(([, id]) => id.trim().length > 0 && !isValidMuxPlaybackId(id))
      .map(([k, id]) => [`AR-city:${k}`, id] as [string, string]);
    return [...country, ...prov, ...cities];
  }, [muxByCountry, muxByArGeo]);

  const invalidPricingEntries = useMemo(
    () =>
      Object.entries(pricingByCountry).filter(([, pricing]) => {
        const priceId = pricing.stripePriceId.trim();
        const productId = pricing.stripeProductId.trim();
        const promoId = pricing.promotionCodeId.trim();
        const fullPrice = Number(pricing.fullPriceUsd);

        if (priceId && !/^price_[A-Za-z0-9]+$/.test(priceId)) return true;
        if (productId && !/^prod_[A-Za-z0-9]+$/.test(productId)) return true;
        if (priceId && (!Number.isFinite(fullPrice) || fullPrice <= 0)) return true;
        if (
          pricing.applyDiscount === "true" &&
          !/^promo_[A-Za-z0-9]+$/.test(promoId)
        ) {
          return true;
        }
        return false;
      }),
    [pricingByCountry],
  );

  const applyForm = useCallback((form: FormState) => {
    setGlobalTpl(form.globalTpl);
    setByCountry(form.byCountry);
    setMuxByCountry(form.muxByCountry);
    setMuxByArGeo(form.muxByArGeo ?? EMPTY_MUX_AR_GEO());
    setPricingByCountry(form.pricingByCountry);
    setGlobalDualCtaFreeTelegram(form.globalDualCtaFreeTelegram);
    setFreeTelegramByCountry(form.freeTelegramByCountry);
  }, []);

  const setArProvinceMux = useCallback((code: string, playbackId: string) => {
    setMuxByArGeo((prev) => {
      const provinces = { ...prev.provinces };
      const trimmed = playbackId.trim();
      if (!trimmed) delete provinces[code];
      else provinces[code] = trimmed;
      return { ...prev, provinces };
    });
  }, []);

  const setArCityMux = useCallback((geoCode: string, playbackId: string) => {
    setMuxByArGeo((prev) => {
      const cities = { ...prev.cities };
      const trimmed = playbackId.trim();
      if (!trimmed) delete cities[geoCode];
      else cities[geoCode] = trimmed;
      return { ...prev, cities };
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/site-settings", { headers });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error((e as { error?: string }).error || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as {
        live?: Record<string, unknown>;
        test?: Record<string, unknown>;
      };
      const liveSrc = (data.live ?? {}) as Record<string, unknown>;
      const testSrc = (data.test ?? liveSrc) as Record<string, unknown>;
      setFull(testSrc);

      const testForm = parseFormFromSettings(testSrc);
      const liveForm = parseFormFromSettings(liveSrc);
      applyForm(testForm);
      savedRef.current = snapshotForm(testForm);
      liveRef.current = snapshotForm(liveForm);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error de red";
      setLoadError(msg);
      toast({
        title: "No se pudo cargar",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [applyForm, getAuthHeaders, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const globalShortLabel = useMemo(() => templateShortLabel(globalTpl), [globalTpl]);

  const overrideCount = Object.keys(byCountry).length;
  const muxOverrideCount = Object.keys(muxByCountry).filter((c) =>
    countryHasActiveMux(muxByCountry[c]),
  ).length;
  const muxAbCountryCount = Object.keys(muxByCountry).filter(
    (c) => activeMuxVariantCount(muxByCountry[c]) > 1,
  ).length;
  /** Countries with a chargeable Stripe Price in the current admin form. */
  const vipStripePriceCount = useMemo(
    () =>
      Object.values(pricingByCountry).filter((p) =>
        isValidStripePriceId(p.stripePriceId),
      ).length,
    [pricingByCountry],
  );
  const vipPromoCount = useMemo(
    () =>
      Object.values(pricingByCountry).filter(
        (p) =>
          p.applyDiscount === "true" &&
          /^promo_[A-Za-z0-9]+$/.test(p.promotionCodeId.trim()),
      ).length,
    [pricingByCountry],
  );

  const templateBreakdown = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const [code, tpl] of Object.entries(byCountry)) {
      const arr = groups.get(tpl) ?? [];
      arr.push(code);
      groups.set(tpl, arr);
    }
    return Array.from(groups, ([template, codes]) => ({
      template,
      codes: codes.sort(),
    })).sort((a, b) => b.codes.length - a.codes.length);
  }, [byCountry]);

  const filteredCountries = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q) {
      return COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
      );
    }

    let base = COUNTRIES;

    if (listTab === "priority") {
      base = PRIORITY_COUNTRIES.map((code) => ({
        code,
        name: COUNTRY_BY_CODE.get(code) ?? code,
      }));
    } else if (listTab === "overrides") {
      const codes = new Set([
        ...Object.keys(byCountry),
        ...Object.keys(muxByCountry),
      ]);
      base = COUNTRIES.filter((c) => codes.has(c.code));
    } else if (listTab === "mux") {
      base = COUNTRIES.filter((c) => countryHasActiveMux(muxByCountry[c.code]));
    }

    return base;
  }, [filter, listTab, byCountry, muxByCountry]);

  const visibleCountries = useMemo(() => {
    if (filter.trim() || listTab !== "all") return filteredCountries;
    return filteredCountries.slice(0, allVisibleLimit);
  }, [filteredCountries, filter, listTab, allVisibleLimit]);

  const dualCtaCountryCodes = useMemo(
    () =>
      COUNTRIES.filter((c) =>
        isDualCtaVideoLandingTemplate(
          effectiveTemplateFor(c.code, globalTpl, byCountry),
        ),
      ).map((c) => c.code),
    [globalTpl, byCountry],
  );

  const jumpToCountry = useCallback((code: string) => {
    setListTab("all");
    setFilter("");
    const idx = COUNTRIES.findIndex((c) => c.code === code);
    if (idx >= 0) {
      setAllVisibleLimit((n) => Math.max(n, idx + 1));
    }
    setEditingCountry(code);
    window.requestAnimationFrame(() => {
      document
        .getElementById(`country-row-${code}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  const setCountry = (code: string, value: string) => {
    const resolvedTpl =
      value === USE_GLOBAL ? globalTpl : normalizeInternationalHomeTemplate(value);

    setByCountry((prev) => {
      const next = { ...prev };
      if (value === USE_GLOBAL) delete next[code];
      else next[code] = normalizeInternationalHomeTemplate(value);
      return next;
    });

    if (
      value === USE_GLOBAL ||
      !internationalHomeTemplateSupportsMuxOverride(resolvedTpl)
    ) {
      setMuxByCountry((prev) => {
        if (!(code in prev)) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      });
    }

    if (value === USE_GLOBAL || !isDualCtaVideoLandingTemplate(resolvedTpl)) {
      setPricingByCountry((prev) => {
        if (!(code in prev)) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setFreeTelegramByCountry((prev) => {
        if (!(code in prev)) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      });
    }
  };

  const setFreeTelegram = (code: string, value: FreeTelegramTriState) =>
    setFreeTelegramByCountry((prev) => {
      if (value === "inherit") {
        if (!(code in prev)) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      }
      return { ...prev, [code]: value };
    });

  const setPricing = (code: string, field: keyof CountryPricingDraft, value: string) =>
    setPricingByCountry((prev) => {
      const cur = prev[code] ?? EMPTY_PRICING_DRAFT();
      let nextEntry: CountryPricingDraft = { ...cur, [field]: value };
      // Unchecking applyDiscount must clear promo + % so save/ensure won't force promo
      if (field === "applyDiscount" && value !== "true") {
        nextEntry = {
          ...nextEntry,
          applyDiscount: "",
          firstMonthDiscountPct: "",
          promotionCodeId: "",
        };
      }
      if (isPricingDraftEmpty(nextEntry)) {
        if (!(code in prev)) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      }
      return { ...prev, [code]: nextEntry };
    });

  /**
   * Crea en Stripe Product + Price (+ Coupon/Promotion Code si hay % off),
   * rellena los IDs del país y **publica live** en el mismo gesto.
   * Sin publish el front sigue en catálogo/fallback (mapa VIP vacío en prod).
   */
  const createStripeVipForCountry = useCallback(
    async (code: string) => {
      const draft = pricingByCountry[code] ?? EMPTY_PRICING_DRAFT();
      const fullUsd = parseFloat(draft.fullPriceUsd);
      if (!Number.isFinite(fullUsd) || fullUsd <= 0) {
        toast({
          title: "Precio USD requerido",
          description: "Ingresá el precio mensual completo antes de crear en Stripe.",
          variant: "destructive",
        });
        return;
      }
      const applyDiscount = draft.applyDiscount === "true";
      const disc = applyDiscount
        ? Math.min(90, Math.max(1, Math.round(parseFloat(draft.firstMonthDiscountPct) || 0)))
        : 0;
      if (applyDiscount && disc <= 0) {
        toast({
          title: "% descuento requerido",
          description: "Activaste cupón 1.er mes: poné un % entre 1 y 90, o desactivá el check.",
          variant: "destructive",
        });
        return;
      }

      setCreatingStripeFor(code);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch("/api/admin/create-country-vip-stripe", {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            country: code,
            fullPriceUsd: fullUsd,
            applyDiscount: applyDiscount && disc > 0,
            firstMonthDiscountPct: disc > 0 ? disc : undefined,
            // This brand only — ignore any foreign product id in the draft
            stripeProductId: "prod_UsCLwRCliyEnz0",
            existingPriceId: draft.stripePriceId.trim() || undefined,
            productName: undefined,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          stripePriceId?: string;
          stripeProductId?: string | null;
          promotionCodeId?: string | null;
          promotionCode?: string | null;
          firstMonthDiscountPct?: number;
          how_it_charges?: string;
        };
        if (!res.ok) {
          throw new Error(data.error || `HTTP ${res.status}`);
        }

        // Explicit next map (avoid setState race before publish payload).
        const nextEntry: CountryPricingDraft = {
          fullPriceUsd: String(fullUsd),
          firstMonthDiscountPct:
            applyDiscount && disc > 0 ? String(disc) : "",
          stripePriceId: data.stripePriceId || draft.stripePriceId,
          stripeProductId:
            data.stripeProductId || "prod_UsCLwRCliyEnz0",
          applyDiscount:
            applyDiscount && data.promotionCodeId ? "true" : "",
          promotionCodeId:
            applyDiscount && data.promotionCodeId
              ? data.promotionCodeId
              : "",
        };
        const nextPricingByCountry: Record<string, CountryPricingDraft> = {
          ...pricingByCountry,
          [code]: nextEntry,
        };
        setPricingByCountry(nextPricingByCountry);

        // Publish immediately so DualCTA front + allowlist charge this price/promo.
        // `full` = site_settings snapshot (not fullUsd) — required for partial-safe save.
        const normalizedGlobal = normalizeInternationalHomeTemplate(globalTpl);
        const normalizedByCountry = Object.fromEntries(
          Object.entries(byCountry).map(([c, tpl]) => [
            c,
            normalizeInternationalHomeTemplate(tpl),
          ]),
        );
        const payload = {
          variant: "test" as const,
          ...full,
          internationalHomeTemplate: normalizedGlobal,
          internationalHomeTemplateByCountry: normalizedByCountry,
          internationalHomeMuxByCountry: pruneInternationalHomeMuxByCountry(
            muxByCountry,
            normalizedGlobal,
            normalizedByCountry,
          ),
          internationalHomeMuxByArGeo: parseInternationalHomeMuxByArGeo(muxByArGeo),
          internationalHomeVipPricingByCountry: pruneInternationalHomeVipPricingByCountry(
            draftPricingToNormalized(nextPricingByCountry),
            normalizedGlobal,
            normalizedByCountry,
          ),
          internationalHomeDualCtaFreeTelegramEnabled: freeTelegramTriToBool(
            globalDualCtaFreeTelegram,
          ),
          internationalHomeDualCtaFreeTelegramByCountry:
            pruneInternationalHomeDualCtaFreeTelegramByCountry(
              draftFreeTelegramToNormalized(freeTelegramByCountry),
              normalizedGlobal,
              normalizedByCountry,
            ),
        };
        const saveRes = await fetch("/api/admin/site-settings", {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!saveRes.ok) {
          const e = await saveRes.json().catch(() => ({}));
          throw new Error(
            (e as { error?: string }).error ||
              "Stripe creado pero no se pudo guardar en site-settings",
          );
        }
        const pubRes = await fetch("/api/admin/site-settings", {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ action: "publish" }),
        });
        if (!pubRes.ok) {
          throw new Error(
            "Stripe + borrador OK, pero falló publicar a live. Usá Guardar (live).",
          );
        }

        const nextForm: FormState = {
          globalTpl,
          byCountry,
          muxByCountry,
          muxByArGeo,
          pricingByCountry: nextPricingByCountry,
          globalDualCtaFreeTelegram,
          freeTelegramByCountry,
        };
        savedRef.current = snapshotForm(nextForm);
        liveRef.current = snapshotForm(nextForm);
        broadcastSiteSettingsUpdated();

        toast({
          title: `${code}: live · cobro real actualizado`,
          description:
            data.how_it_charges ||
            `Price ${data.stripePriceId}${
              data.promotionCodeId
                ? ` + promo ${data.promotionCode || data.promotionCodeId}`
                : ""
            }. DualCTA ya resuelve este país en el front (sin deploy).`,
        });
        void load();
      } catch (e) {
        toast({
          title: "No se pudo crear / publicar VIP Stripe",
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        });
      } finally {
        setCreatingStripeFor(null);
      }
    },
    [
      pricingByCountry,
      getAuthHeaders,
      toast,
      globalTpl,
      byCountry,
      muxByCountry,
      muxByArGeo,
      full,
      globalDualCtaFreeTelegram,
      freeTelegramByCountry,
      load,
    ],
  );

  const setMuxVariants = useCallback((code: string, variants: MuxCountryVariant[]) => {
    const packed = packMuxCountryDraft(variants);
    setMuxByCountry((prev) => {
      const next = { ...prev };
      if (!packed) delete next[code];
      else next[code] = packed;
      return next;
    });
  }, []);

  /**
   * Asignar playback Mux a un país: si la plantilla efectiva no soporta Mux,
   * activa DualCTA automáticamente para que el video se vea en `/`.
   * Si ya hay A/B, actualiza la primera variante; si no, setea string simple.
   */
  const assignMuxToCountry = useCallback(
    (code: string, playbackId: string) => {
      const trimmed = playbackId.trim();
      const effectiveTpl = effectiveTemplateFor(code, globalTpl, byCountry);
      if (trimmed && !internationalHomeTemplateSupportsMuxOverride(effectiveTpl)) {
        setByCountry((prev) => ({
          ...prev,
          [code]: DUAL_CTA_VIDEO_LANDING_TEMPLATE,
        }));
        toast({
          title: `${code}: Dual CTA + Mux`,
          description:
            "La plantilla no mostraba video; se activó DualCTA para este país y se guardó el playback ID.",
        });
      }
      setMuxByCountry((prev) => {
        const next = { ...prev };
        if (!trimmed) {
          delete next[code];
          return next;
        }
        const existing = getCountryVariantsListDraft(prev, code);
        if (existing.length > 1) {
          const updated = existing.map((v, i) =>
            i === 0 ? { ...v, playbackId: trimmed, enabled: true } : v,
          );
          const packed = packMuxCountryDraft(updated);
          if (packed) next[code] = packed;
          else delete next[code];
        } else {
          next[code] = trimmed;
        }
        return next;
      });
    },
    [globalTpl, byCountry, toast],
  );

  /** Ensure DualCTA when adding Mux variants to a non-Mux template. */
  const setMuxVariantsWithTemplateGuard = useCallback(
    (code: string, variants: MuxCountryVariant[]) => {
      const hasAny = variants.some((v) => isValidMuxPlaybackId(v.playbackId));
      if (hasAny) {
        const effectiveTpl = effectiveTemplateFor(code, globalTpl, byCountry);
        if (!internationalHomeTemplateSupportsMuxOverride(effectiveTpl)) {
          setByCountry((prev) => ({
            ...prev,
            [code]: DUAL_CTA_VIDEO_LANDING_TEMPLATE,
          }));
          toast({
            title: `${code}: Dual CTA + Mux`,
            description:
              "La plantilla no mostraba video; se activó DualCTA para este país.",
          });
        }
      }
      setMuxVariants(code, variants);
    },
    [globalTpl, byCountry, toast, setMuxVariants],
  );

  /** Países en el panel rápido Mux: prioritarios + los que ya tienen override. */
  const muxManageCountries = useMemo(() => {
    const codes = new Set<string>([...PRIORITY_COUNTRIES]);
    for (const c of Object.keys(muxByCountry)) {
      if (countryHasActiveMux(muxByCountry[c])) codes.add(c.toUpperCase());
    }
    return COUNTRIES.filter((c) => codes.has(c.code));
  }, [muxByCountry]);

  const applyMuxNativeToCountry = (code: string) => {
    setCountry(code, INVITACION_EXCLUSIVA_MUX_TEMPLATE);
  };

  const applyDualCtaToCountry = (code: string) => {
    setCountry(code, DUAL_CTA_VIDEO_LANDING_TEMPLATE);
  };

  const applyRecommendedGlobal = () => {
    setGlobalTpl(DUAL_CTA_VIDEO_LANDING_TEMPLATE);
    toast({
      title: "Dual CTA global",
      description: "Todos los países sin override verán Mux + membresía Stripe + canal gratis.",
    });
  };

  const applyMuxNativeToPriorityIntl = () => {
    setByCountry((prev) => {
      const next = { ...prev };
      for (const code of PRIORITY_COUNTRIES) {
        if (code === "AR") continue;
        next[code] = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
      }
      return next;
    });
    toast({
      title: "Países prioritarios actualizados",
      description: "US, MX, ES, BR, GB, DE, FR, CO, CL → Dual CTA (AR sin cambios).",
    });
  };

  const applyDualCtaToAllIntl = () => {
    setByCountry((prev) => {
      const next = { ...prev };
      for (const { code } of COUNTRIES) {
        if (code === "AR") continue;
        next[code] = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
      }
      return next;
    });
    toast({
      title: "Dual CTA en todos",
      description: `${COUNTRIES.length - 1} países internacionales (AR sin cambios).`,
    });
  };

  /**
   * ISO2 targets for bulk VIP: every non-AR country in the admin list
   * (world + LATAM). Global DualCTA already covers them at template level;
   * the map makes each ISO2 explicit for allowlist + admin audit.
   */
  const allIntlCountryCodes = useMemo(
    () => COUNTRIES.map((c) => c.code).filter((code) => code !== "AR"),
    [],
  );

  const applyGlobalPricingToAllDualCta = () => {
    const full = globalPricingDraft.fullPriceUsd.trim() || String(FALLBACK_VIP_DISPLAY_PRICE_USD);
    const priceId =
      globalPricingDraft.stripePriceId.trim() || FALLBACK_VIP_STRIPE_PRICE_ID;
    if (!full) {
      toast({
        title: "Precio requerido",
        description: "Ingresá el precio display USD en la sección global Dual CTA.",
        variant: "destructive",
      });
      return;
    }
    if (!isValidStripePriceId(priceId)) {
      toast({
        title: "Stripe Price ID requerido",
        description:
          "Pegá un price_… (ej. price_1TsRwOH8xCanmtKXBEW8SAea) para que el bulk cobre en Stripe.",
        variant: "destructive",
      });
      return;
    }
    const applyDisc = globalPricingDraft.applyDiscount === "true";
    const promoId =
      applyDisc
        ? globalPricingDraft.promotionCodeId.trim() ||
          FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID
        : "";
    if (applyDisc && !/^promo_[A-Za-z0-9]+$/.test(promoId)) {
      toast({
        title: "Promotion Code inválido",
        description: "Con descuento activo hace falta un promo_… válido (ANA70).",
        variant: "destructive",
      });
      return;
    }
    const entry: CountryPricingDraft = {
      ...EMPTY_PRICING_DRAFT(),
      fullPriceUsd: full,
      firstMonthDiscountPct: applyDisc
        ? globalPricingDraft.firstMonthDiscountPct.trim() ||
          String(FALLBACK_VIP_DISPLAY_DISCOUNT_PCT)
        : "",
      stripePriceId: priceId,
      stripeProductId:
        globalPricingDraft.stripeProductId.trim() || "prod_UsCLwRCliyEnz0",
      applyDiscount: applyDisc ? "true" : "",
      promotionCodeId: applyDisc ? promoId : "",
    };
    // Apply to ALL international countries (not only dualCtaCountryCodes list).
    setPricingByCountry((prev) => {
      const next = { ...prev };
      for (const code of allIntlCountryCodes) {
        next[code] = { ...entry };
      }
      return next;
    });
    // Ensure DualCTA template for all intl so prune keeps every VIP row.
    setGlobalTpl(DUAL_CTA_VIDEO_LANDING_TEMPLATE);
    setByCountry((prev) => {
      const next = { ...prev };
      for (const code of allIntlCountryCodes) {
        next[code] = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
      }
      return next;
    });
    const disc = entry.firstMonthDiscountPct;
    toast({
      title: "Precio VIP en todos los países",
      description: `${allIntlCountryCodes.length} países (sin AR) → ${priceId}${
        entry.applyDiscount === "true" && entry.promotionCodeId
          ? ` + ${entry.promotionCodeId}`
          : ""
      }${disc ? ` · −${disc}%` : ""}. Usá «Guardar (live)» para publicar.`,
    });
  };

  /** Bulk VIP map + DualCTA for every non-AR country, then publish live. */
  const applyGlobalPricingToAllAndPublish = async () => {
    const fullUsd =
      globalPricingDraft.fullPriceUsd.trim() || String(FALLBACK_VIP_DISPLAY_PRICE_USD);
    const priceId =
      globalPricingDraft.stripePriceId.trim() || FALLBACK_VIP_STRIPE_PRICE_ID;
    const applyDisc = globalPricingDraft.applyDiscount === "true";
    const promoId = applyDisc
      ? globalPricingDraft.promotionCodeId.trim() ||
        FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID
      : "";
    if (!isValidStripePriceId(priceId)) {
      toast({
        title: "Stripe Price ID requerido",
        description: "Pegá un price_… válido antes de publicar a todos.",
        variant: "destructive",
      });
      return;
    }
    if (applyDisc && !/^promo_[A-Za-z0-9]+$/.test(promoId)) {
      toast({
        title: "Promotion Code inválido",
        description: "Con descuento activo hace falta un promo_… válido.",
        variant: "destructive",
      });
      return;
    }

    const entry: CountryPricingDraft = {
      ...EMPTY_PRICING_DRAFT(),
      fullPriceUsd: fullUsd,
      firstMonthDiscountPct: applyDisc
        ? globalPricingDraft.firstMonthDiscountPct.trim() ||
          String(FALLBACK_VIP_DISPLAY_DISCOUNT_PCT)
        : "",
      stripePriceId: priceId,
      stripeProductId:
        globalPricingDraft.stripeProductId.trim() || "prod_UsCLwRCliyEnz0",
      applyDiscount: applyDisc ? "true" : "",
      promotionCodeId: applyDisc ? promoId : "",
    };
    const nextPricing: Record<string, CountryPricingDraft> = {};
    for (const code of allIntlCountryCodes) {
      nextPricing[code] = { ...entry };
    }
    const nextByCountry: Record<string, string> = { ...byCountry };
    for (const code of allIntlCountryCodes) {
      nextByCountry[code] = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
    }

    setPricingByCountry(nextPricing);
    setGlobalTpl(DUAL_CTA_VIDEO_LANDING_TEMPLATE);
    setByCountry(nextByCountry);

    const normalizedGlobal = DUAL_CTA_VIDEO_LANDING_TEMPLATE;
    const normalizedByCountry = Object.fromEntries(
      Object.entries(nextByCountry).map(([c, tpl]) => [
        c,
        normalizeInternationalHomeTemplate(tpl),
      ]),
    );

    try {
      setPublishing(true);
      const headers = await getAuthHeaders();
      const payload = {
        variant: "test" as const,
        ...full,
        internationalHomeTemplate: normalizedGlobal,
        internationalHomeTemplateByCountry: normalizedByCountry,
        internationalHomeMuxByCountry: pruneInternationalHomeMuxByCountry(
          muxByCountry,
          normalizedGlobal,
          normalizedByCountry,
        ),
        internationalHomeMuxByArGeo: parseInternationalHomeMuxByArGeo(muxByArGeo),
        internationalHomeVipPricingByCountry: pruneInternationalHomeVipPricingByCountry(
          draftPricingToNormalized(nextPricing),
          normalizedGlobal,
          normalizedByCountry,
        ),
        internationalHomeDualCtaFreeTelegramEnabled: freeTelegramTriToBool(
          globalDualCtaFreeTelegram,
        ),
        internationalHomeDualCtaFreeTelegramByCountry:
          pruneInternationalHomeDualCtaFreeTelegramByCountry(
            draftFreeTelegramToNormalized(freeTelegramByCountry),
            normalizedGlobal,
            normalizedByCountry,
          ),
      };
      const saveRes = await fetch("/api/admin/site-settings", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!saveRes.ok) {
        const e = await saveRes.json().catch(() => ({}));
        throw new Error((e as { error?: string }).error || "No se pudo guardar");
      }
      const pubRes = await fetch("/api/admin/site-settings", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "publish" }),
      });
      if (!pubRes.ok) throw new Error("Guardado OK pero falló publicar a live");

      const nextForm: FormState = {
        globalTpl: normalizedGlobal,
        byCountry: nextByCountry,
        muxByCountry,
        muxByArGeo,
        pricingByCountry: nextPricing,
        globalDualCtaFreeTelegram,
        freeTelegramByCountry,
      };
      savedRef.current = snapshotForm(nextForm);
      liveRef.current = snapshotForm(nextForm);
      broadcastSiteSettingsUpdated();
      toast({
        title: "VIP live en todos los países",
        description: `${allIntlCountryCodes.length} países → ${priceId}${
          applyDisc && promoId ? ` + ${promoId}` : ""
        }. Front DualCTA ya resuelve el cobro (sin deploy de admin).`,
      });
      void load();
    } catch (err) {
      toast({
        title: "Error al publicar VIP global",
        description: err instanceof Error ? err.message : "No se pudo publicar",
        variant: "destructive",
      });
    } finally {
      setPublishing(false);
    }
  };

  const applyGlobalFreeTelegramToAllDualCta = () => {
    if (globalDualCtaFreeTelegram === "inherit") {
      toast({
        title: "Elegí Mostrar u Ocultar",
        description: "En global Dual CTA, definí el toggle antes de propagar a todos.",
        variant: "destructive",
      });
      return;
    }
    setFreeTelegramByCountry((prev) => {
      const next = { ...prev };
      for (const code of dualCtaCountryCodes) {
        next[code] = globalDualCtaFreeTelegram;
      }
      return next;
    });
    toast({
      title: "Telegram gratis propagado",
      description: `${dualCtaCountryCodes.length} país(es) Dual CTA → ${globalDualCtaFreeTelegram === "on" ? "mostrar" : "ocultar"}.`,
    });
  };

  const post = async (extra: Record<string, unknown>) => {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/site-settings", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(extra),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error((e as { error?: string }).error || "Operación fallida");
    }
  };

  const buildPayload = () => {
    const normalizedGlobal = normalizeInternationalHomeTemplate(globalTpl);
    const normalizedByCountry = Object.fromEntries(
      Object.entries(byCountry).map(([code, tpl]) => [
        code,
        normalizeInternationalHomeTemplate(tpl),
      ]),
    );
    return {
      variant: "test" as const,
      ...full,
      internationalHomeTemplate: normalizedGlobal,
      internationalHomeTemplateByCountry: normalizedByCountry,
      internationalHomeMuxByCountry: pruneInternationalHomeMuxByCountry(
        muxByCountry,
        normalizedGlobal,
        normalizedByCountry,
      ),
      internationalHomeMuxByArGeo: parseInternationalHomeMuxByArGeo(muxByArGeo),
      internationalHomeVipPricingByCountry: pruneInternationalHomeVipPricingByCountry(
        draftPricingToNormalized(pricingByCountry),
        normalizedGlobal,
        normalizedByCountry,
      ),
      internationalHomeDualCtaFreeTelegramEnabled: freeTelegramTriToBool(
        globalDualCtaFreeTelegram,
      ),
      internationalHomeDualCtaFreeTelegramByCountry:
        pruneInternationalHomeDualCtaFreeTelegramByCountry(
          draftFreeTelegramToNormalized(freeTelegramByCountry),
          normalizedGlobal,
          normalizedByCountry,
        ),
    };
  };

  const validateBeforeSave = (): boolean => {
    if (invalidMuxEntries.length > 0) {
      toast({
        title: "Playback IDs inválidos",
        description: `Corregí ${invalidMuxEntries.length} país(es) antes de guardar: ${invalidMuxEntries.map(([c]) => c).join(", ")}`,
        variant: "destructive",
      });
      return false;
    }
    if (invalidPricingEntries.length > 0) {
      toast({
        title: "Configuración Stripe inválida",
        description:
          "Revisá Price ID (price_…), Product ID (prod_…), precio USD y Promotion Code (promo_…) en: " +
          invalidPricingEntries.map(([country]) => country).join(", "),
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleDiscard = () => {
    if (!savedRef.current) return;
    applyForm(JSON.parse(savedRef.current) as FormState);
    toast({ title: "Cambios descartados" });
  };

  const saveDraft = async () => {
    if (!validateBeforeSave()) return;
    try {
      setSavingDraft(true);
      await post(buildPayload());
      savedRef.current = snapshotForm(currentForm);
      toast({
        title: "Borrador guardado",
        description: "Aún no está en producción. Usá «Publicar» para aplicar en vivo.",
      });
      void load();
    } catch (err) {
      toast({
        title: "Error al guardar",
        description: err instanceof Error ? err.message : "No se pudo guardar",
        variant: "destructive",
      });
    } finally {
      setSavingDraft(false);
    }
  };

  /** Guardar = live al instante: escribe borrador + publica a base_template + broadcast SPA. */
  const saveAndPublish = async () => {
    if (!validateBeforeSave()) return;
    try {
      setPublishing(true);
      // Si el video DualCTA global está dirty, lo publicamos en el mismo gesto.
      if (globalDefaultMuxDirty) {
        const ok = await saveGlobalDefaultMux({ silent: true });
        if (!ok) {
          setPublishing(false);
          return;
        }
      }
      await post(buildPayload());
      await post({ action: "publish" });
      savedRef.current = snapshotForm(currentForm);
      liveRef.current = snapshotForm(currentForm);
      broadcastSiteSettingsUpdated();
      toast({
        title: "Guardado en producción (live)",
        description:
          "Mux (global + por país), plantilla DualCTA y VIP Stripe ya aplican en `/`. Video al init del visitante actualizado sin deploy.",
      });
      void load();
      void loadGlobalDefaultMux();
    } catch (err) {
      toast({
        title: "Error al publicar",
        description: err instanceof Error ? err.message : "No se pudo publicar",
        variant: "destructive",
      });
    } finally {
      setPublishing(false);
    }
  };

  const resetAllOverrides = () => {
    setByCountry({});
    setMuxByCountry({});
    setMuxByArGeo(EMPTY_MUX_AR_GEO());
    setPricingByCountry({});
    setFreeTelegramByCountry({});
    toast({ title: "Overrides limpiados", description: "Todos los países volverán al global al publicar." });
  };

  const arGeoCounts = useMemo(
    () => countArGeoMuxOverrides(muxByArGeo),
    [muxByArGeo],
  );

  const filteredArCities = useMemo(() => {
    const q = arCityFilter.trim().toLowerCase();
    if (!q) return AR_CITY_CATALOG.slice(0, 40);
    return AR_CITY_CATALOG.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.geo_code.includes(q) ||
        c.provinceCode.toLowerCase().includes(q),
    ).slice(0, 60);
  }, [arCityFilter]);

  const arOverride = byCountry["AR"];
  const editingCountryMeta = editingCountry
    ? COUNTRIES.find((c) => c.code === editingCountry)
    : null;
  const previewEffectiveTpl = effectiveTemplateFor(
    previewCountry,
    globalTpl,
    byCountry,
  );
  const previewVideoOnInit = isMuxVideoPlayerHomeTemplate(previewEffectiveTpl);

  const rowProps = {
    globalTpl,
    byCountry,
    muxByCountry,
    pricingByCountry,
    freeTelegramByCountry,
    onSetCountry: setCountry,
    onSetMuxVariants: setMuxVariantsWithTemplateGuard,
    onSetPricing: setPricing,
    onCreateStripeVip: createStripeVipForCountry,
    creatingStripeFor,
    onSetFreeTelegram: setFreeTelegram,
    onApplyMux: applyMuxNativeToCountry,
    onApplyDualCta: applyDualCtaToCountry,
    onEditMobile: setEditingCountry,
    editingCountry,
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <AdminPageShell width="md" className="pb-28">
      <AdminPageHeader
        description={
          <>
            <strong>DualCTAVideoLanding</strong> (home no-AR): elegí{" "}
            <strong>Mux playback</strong> → video del visitante, y{" "}
            <strong>Stripe price_…</strong> → cobro de Membresía Exclusiva.{" "}
            <strong>Guardar (live)</strong> = prod al instante. Atajos:{" "}
            <code className="text-[10px]">/admin/dual-cta</code>.
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {hasUnsavedChanges && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-600">
                Sin guardar
              </Badge>
            )}
            {hasUnpublishedOnServer && !hasUnsavedChanges && (
              <Badge variant="outline" className="border-sky-500/40 text-sky-600">
                Borrador pendiente
              </Badge>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/premium-invite-config">Fallback global Mux</Link>
            </Button>
            <GeoSimCountryPreviewButton
              country={previewCountry}
              source="country_templates_header"
              size="sm"
              label={
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  GeoSim {previewCountry}
                </>
              }
              showIcon={false}
              title="Ver DualCTA como visitante real de este país (edge)"
            />
          </div>
        }
      />

      {/* Pipeline DualCTA: Mux → Stripe → visitante */}
      <Card className="border-violet-500/25 bg-gradient-to-r from-violet-500/[0.07] via-amber-500/[0.04] to-transparent">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-violet-600" />
            Pipeline DualCTA (admin → visitante)
          </CardTitle>
          <CardDescription className="text-xs leading-relaxed">
            Cada país ISO2 resuelve en runtime así. Lo que configurás acá pisa fallbacks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ol className="grid gap-2 text-xs sm:grid-cols-3">
            <li className="rounded-lg border bg-background/80 p-3">
              <p className="font-semibold text-amber-700 dark:text-amber-400">1 · Mux video</p>
              <p className="mt-1 text-muted-foreground">
                Playback ID por país (A/B). Vacío → Invit. premium / env.
              </p>
              <p className="mt-1.5 font-mono text-[10px] text-foreground/80">
                overrides: <strong>{muxOverrideCount}</strong>
              </p>
            </li>
            <li className="rounded-lg border bg-background/80 p-3">
              <p className="font-semibold text-emerald-700 dark:text-emerald-400">2 · Stripe VIP</p>
              <p className="mt-1 text-muted-foreground">
                <code className="text-[10px]">price_…</code> (+ promo 1.er mes). Vacío → catálogo /
                FALLBACK.
              </p>
              <p className="mt-1.5 font-mono text-[10px] text-foreground/80">
                con price: <strong>{vipStripePriceCount}</strong>
                {vipPromoCount > 0 ? (
                  <>
                    {" · "}promo: <strong>{vipPromoCount}</strong>
                  </>
                ) : null}
              </p>
            </li>
            <li className="rounded-lg border bg-background/80 p-3">
              <p className="font-semibold text-violet-700 dark:text-violet-400">3 · Ver visitante</p>
              <p className="mt-1 text-muted-foreground">
                GeoSim edge: template DualCTA + video + CTA Stripe del país.
              </p>
              <div className="mt-2">
                <GeoSimCountryPreviewButton
                  country={previewCountry}
                  source="country_templates_pipeline"
                  size="sm"
                  className="h-8"
                  label={`Preview ${previewCountry}`}
                />
              </div>
            </li>
          </ol>
          {muxOverrideCount === 0 && !globalDefaultMux.trim() && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-900 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                <strong>Sin video DualCTA configurado.</strong> Hoy el visitante cae al
                fallback env. Elegí el video en la card de abajo y guardá live.
              </span>
            </div>
          )}
          {isMuxVideoPlayerHomeTemplate(globalTpl) && vipStripePriceCount === 0 && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-950 dark:text-emerald-100">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                <strong>Mapa VIP vacío en admin</strong> — el front igual aplica la oferta
                canónica a <em>todos</em> los países:{" "}
                <code className="text-[10px]">{FALLBACK_VIP_STRIPE_PRICE_ID}</code> + ANA70 (−
                {FALLBACK_VIP_DISPLAY_DISCOUNT_PCT}%). Para fijar el mapa por ISO2 usá{" "}
                <em>Aplicar VIP a todos y publicar live</em> (sección global abajo).
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ HERO: video DualCTA al init (gestión inmediata) ═══ */}
      <Card className="border-2 border-amber-500/40 bg-gradient-to-b from-amber-500/[0.09] to-transparent shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <PlayCircle className="h-5 w-5 text-amber-600" />
            Video DualCTA al init
            <Badge className="bg-amber-600 text-[10px] font-normal hover:bg-amber-600">
              decide qué se reproduce
            </Badge>
          </CardTitle>
          <CardDescription className="text-xs leading-relaxed">
            En países con plantilla <strong>DualCTAVideoLanding</strong> el video{" "}
            <strong>empieza solo al abrir</strong> (autoplay). Este playback es el default
            global; cada país abajo puede overridear. Guardar = prod sin deploy.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <MuxVideoPicker
            value={globalDefaultMux}
            onChange={setGlobalDefaultMux}
            placeholder="Playback ID DualCTA (vacío = env de marca)"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="h-10"
              onClick={() => void saveGlobalDefaultMux()}
              disabled={
                savingDefaultMux ||
                publishing ||
                !globalDefaultMuxDirty ||
                !globalDefaultMuxValid
              }
            >
              {savingDefaultMux ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Guardando…
                </>
              ) : (
                <>
                  <UploadCloud className="mr-1.5 h-3.5 w-3.5" />
                  Guardar video (live)
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-10 bg-amber-600 hover:bg-amber-700"
              onClick={() => void applyDefaultMuxToPriorityLive()}
              disabled={
                savingDefaultMux ||
                publishing ||
                !globalDefaultMuxValid ||
                !globalDefaultMux.trim()
              }
              title="Fija DualCTA + este playback en prioritarios intl y publica live"
            >
              {publishing ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Publicando…
                </>
              ) : (
                <>
                  <Zap className="mr-1.5 h-3.5 w-3.5" />
                  Aplicar a prioritarios + live
                </>
              )}
            </Button>
            <GeoSimCountryPreviewButton
              country={previewCountry}
              source="dual_cta_default_mux"
              size="sm"
              className="h-10"
              label={`Ver init ${previewCountry}`}
              title="GeoSim: DualCTA como visitante — video al init + Stripe"
            />
            {globalDefaultMuxDirty && globalDefaultMuxValid && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-600">
                Sin guardar
              </Badge>
            )}
            {!globalDefaultMuxValid && (
              <Badge variant="destructive">Playback ID inválido</Badge>
            )}
            {globalDefaultMux.trim() && !globalDefaultMuxDirty && (
              <Badge
                variant="outline"
                className="border-emerald-500/40 text-[10px] text-emerald-600"
              >
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Live
              </Badge>
            )}
          </div>
          <p className="text-[10px] leading-snug text-muted-foreground">
            <strong className="text-foreground/80">Prioridad:</strong> Mux del país (abajo) →{" "}
            <strong>este video</strong> → env.{" "}
            <strong className="text-foreground/80">Aplicar a prioritarios</strong> escribe el
            mismo ID en US, MX, CL, ES, BR… (no AR) + DualCTA + publish.
          </p>
        </CardContent>
      </Card>

      <ConfigOwnerCallout
        variant="owner"
        owns="DualCTAVideoLanding: video al init (Mux) + VIP Stripe + free TG por ISO2. Guardar (live) = test→live + invalida cache SPA."
        notHere="No uses Invit. premium ni Suscripciones para el mismo país si ya hay override acá — este page gana en runtime."
        priority={[
          "Video al init: mux_by_country → AR geo → simple_mux_overlay (esta card) → env",
          "Cobro: vip_pricing_by_country[ISO2] → VIP default compacto (API pública) → FALLBACK price_/ANA70",
          "API pública colapsa 200 precios idénticos a internationalHomeVipPricingDefault (admin sigue viendo el mapa completo)",
          "QA: GeoSim por país · data-dual-cta-playback-id · after publish landings hard-refetch site-settings",
        ]}
      />

      {loadError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {/* Configuración rápida */}
      <Card className="border-primary/20 bg-primary/[0.03]">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-primary" />
            Configuración recomendada
          </CardTitle>
          <CardDescription>
            Internacional: video Mux al abrir + checkout Stripe. Un toque para el setup
            más usado en mobile.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button className="h-11 min-h-[44px] flex-1 sm:flex-none" onClick={applyRecommendedGlobal}>
            <Video className="mr-2 h-4 w-4" />
            Mux nativo global
          </Button>
          <Button
            variant="secondary"
            className="h-11 min-h-[44px] flex-1 sm:flex-none"
            onClick={applyMuxNativeToPriorityIntl}
          >
            <Globe className="mr-2 h-4 w-4" />
            Mux en prioritarios
          </Button>
          <GeoSimCountryPreviewButton
            country={previewCountry}
            source="country_templates_mobile_cta"
            className="h-11 min-h-[44px] flex-1 sm:flex-none"
            label={
              <>
                <Eye className="mr-2 h-4 w-4" />
                Probar GeoSim
              </>
            }
            showIcon={false}
            title={`GeoSim edge · ${previewCountry} (no SPA ?country=)`}
          />
        </CardContent>
      </Card>

      <CountryTemplateMobilePreview
        countryCode={previewCountry}
        countries={COUNTRIES}
        onCountryChange={setPreviewCountry}
        effectiveTemplateLabel={templateShortLabel(previewEffectiveTpl)}
        videoOnInit={previewVideoOnInit}
      />

      {/* ── DualCTA: Mux + Stripe por país (panel principal) ──────────────── */}
      <Card className="border-amber-500/30 bg-gradient-to-b from-amber-500/[0.06] to-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <PlayCircle className="h-4 w-4 text-amber-600" />
            DualCTA · Mux video + Stripe VIP por país
          </CardTitle>
          <CardDescription>
            Por cada ISO2: <strong>playback Mux</strong> (qué video) y{" "}
            <strong>price_… Stripe</strong> (qué cobra el CTA). A/B multi-video con
            peso + <code className="text-[10px]">mux_variant_key</code> en PostHog.
            Vacío Mux →{" "}
            <Link
              to="/admin/premium-invite-config"
              className="underline underline-offset-2"
            >
              Invit. premium
            </Link>
            . Vacío Stripe → catálogo / FALLBACK. Al poner playback se fuerza plantilla{" "}
            <strong>Dual CTA</strong>. <strong>Guardar (live)</strong> = prod.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="secondary" className="font-normal">
              {muxOverrideCount} país(es) con video propio
            </Badge>
            {muxAbCountryCount > 0 && (
              <Badge
                variant="outline"
                className="border-violet-500/40 font-normal text-violet-600"
              >
                {muxAbCountryCount} con A/B multi-video
              </Badge>
            )}
            <span>
              Prioritarios: {PRIORITY_COUNTRIES.join(", ")} + cualquier otro con
              override
            </span>
          </div>
          <div className="max-h-[min(640px,70dvh)] space-y-3 overflow-y-auto pr-1">
            {muxManageCountries.map(({ code, name }) => {
              const variants = getCountryVariantsListDraft(muxByCountry, code);
              const pid = firstMuxPlaybackId(muxByCountry[code]);
              const variantN = activeMuxVariantCount(muxByCountry[code]);
              const effectiveTpl = effectiveTemplateFor(code, globalTpl, byCountry);
              const muxReady =
                internationalHomeTemplateSupportsMuxOverride(effectiveTpl);
              const valid = isValidMuxPlaybackId(pid);
              return (
                <div
                  key={code}
                  className={cn(
                    "rounded-xl border bg-card/80 p-3",
                    valid && "border-amber-500/25",
                    variantN > 1 && "border-violet-500/30",
                  )}
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="text-sm font-semibold tracking-wide">
                        {code}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {name}
                      </span>
                      {valid && (
                        <Badge
                          variant="outline"
                          className="border-emerald-500/40 text-[10px] text-emerald-600"
                        >
                          activo
                        </Badge>
                      )}
                      {variantN > 1 && (
                        <Badge
                          variant="outline"
                          className="border-violet-500/40 text-[10px] text-violet-600"
                        >
                          A/B · {variantN}
                        </Badge>
                      )}
                      {!muxReady && (
                        <Badge
                          variant="outline"
                          className="border-sky-500/40 text-[10px] text-sky-600"
                        >
                          al guardar → Dual CTA
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => setPreviewCountry(code)}
                      >
                        Preview
                      </Button>
                      {pid && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-muted-foreground"
                          onClick={() => assignMuxToCountry(code, "")}
                        >
                          Quitar todo
                        </Button>
                      )}
                    </div>
                  </div>
                  <CountryMuxVariantsEditor
                    code={code}
                    variants={variants}
                    onChange={(next) => setMuxVariantsWithTemplateGuard(code, next)}
                  />
                  {/* Stripe VIP del mismo país — cobro DualCTA Membresía Exclusiva */}
                  {(() => {
                    const pricing = pricingByCountry[code] ?? EMPTY_PRICING_DRAFT();
                    const priceOk = isValidStripePriceId(pricing.stripePriceId);
                    const offer = resolveInternationalHomeOffer({
                      country: code,
                      muxByCountry: parseInternationalHomeMuxByCountry(muxByCountry),
                      vipPricingByCountry: draftPricingToNormalized(pricingByCountry),
                      playbackIdOverride: pid || null,
                    });
                    return (
                      <div className="mt-3 space-y-2 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.04] p-2.5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300">
                            <CreditCard className="mr-1 inline h-3.5 w-3.5" />
                            Stripe VIP · Membresía Exclusiva
                          </p>
                          <GeoSimCountryPreviewButton
                            country={code}
                            source={`dual_cta_mux_card_${code}`}
                            size="sm"
                            className="h-7 text-[10px]"
                            label="Ver DualCTA"
                            title={`GeoSim ${code}: video + checkout como visitante`}
                          />
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <div className="space-y-1">
                            <label className="text-[10px] text-muted-foreground">
                              Stripe Price ID (price_…)
                            </label>
                            <Input
                              type="text"
                              placeholder="price_…"
                              value={pricing.stripePriceId}
                              onChange={(e) =>
                                setPricing(code, "stripePriceId", e.target.value)
                              }
                              className="h-8 font-mono text-xs"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-muted-foreground">
                              Display USD / mes
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="49.99"
                              value={pricing.fullPriceUsd}
                              onChange={(e) =>
                                setPricing(code, "fullPriceUsd", e.target.value)
                              }
                              className="h-8 font-mono text-xs"
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="h-8 text-[10px]"
                            disabled={
                              creatingStripeFor === code ||
                              !pricing.fullPriceUsd.trim() ||
                              !(parseFloat(pricing.fullPriceUsd) > 0)
                            }
                            onClick={() => void createStripeVipForCountry(code)}
                          >
                            {creatingStripeFor === code ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : (
                              <CreditCard className="mr-1 h-3 w-3" />
                            )}
                            Crear Price en Stripe
                          </Button>
                          {!priceOk && (
                            <span className="text-[10px] text-amber-700 dark:text-amber-300">
                              Sin price_ el cobro usa catálogo/fallback
                            </span>
                          )}
                        </div>
                        <p className="font-mono text-[10px] text-muted-foreground">
                          Runtime: Mux{" "}
                          <span className="text-amber-700 dark:text-amber-300">
                            {offer.playbackSource}
                          </span>
                          {" · cobra "}
                          <span className="text-emerald-700 dark:text-emerald-300">
                            {offer.pricing.stripePriceId || "(catálogo)"}
                          </span>
                          {" · "}
                          {offer.pricing.priceSource}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Más países: lista de abajo o editor. AR: Mux por provincia/ciudad en Resumen.
            Preview real del visitante = botón <strong>Ver DualCTA</strong> (GeoSim).
          </p>
        </CardContent>
      </Card>

      {/* Estado + Argentina */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Resumen</CardTitle>
          <CardDescription>
            Producción actual: <strong>{globalShortLabel}</strong> como global
            {overrideCount > 0 ? ` · ${overrideCount} país(es) con override` : ""}
            {muxOverrideCount > 0 ? ` · ${muxOverrideCount} con video propio` : ""}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="text-2xl font-bold">{overrideCount}</div>
              <div className="text-xs text-muted-foreground">Overrides de plantilla</div>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="text-2xl font-bold text-amber-600">{muxOverrideCount}</div>
              <div className="text-xs text-muted-foreground">Videos Mux por país</div>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="text-2xl font-bold">{COUNTRIES.length - overrideCount}</div>
              <div className="text-xs text-muted-foreground">Países en plantilla global</div>
            </div>
          </div>

          {templateBreakdown.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {templateBreakdown.map(({ template, codes }) => (
                <Badge key={template} variant={templateBadgeVariant(template)} className="gap-1">
                  {templateShortLabel(template)}
                  <span className="opacity-70">×{codes.length}</span>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🇦🇷</span>
              <div>
                <p className="text-sm font-medium">Argentina</p>
                <p className="text-xs text-muted-foreground">
                  {arOverride
                    ? `${templateShortLabel(arOverride)} activo — se ignoran CTAs por provincia`
                    : "BaseTemplate del Worker (CTAs por provincia / wishlist)"}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {arOverride ? (
                <Button variant="ghost" size="sm" onClick={() => setCountry("AR", USE_GLOBAL)}>
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Volver al Worker
                </Button>
              ) : (
                <>
                  <Button size="sm" onClick={() => applyMuxNativeToCountry("AR")}>
                    <Sparkles className="mr-1 h-3 w-3" />
                    Mux nativo
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => applyDualCtaToCountry("AR")}>
                    Dual CTA
                  </Button>
                </>
              )}
              <GeoSimCountryPreviewButton
                country="AR"
                source="country_templates_ar_card"
                variant="ghost"
                size="sm"
                label="Preview AR"
                title="GeoSim edge · visitante Argentina (Worker + CTA por región)"
              />
            </div>
          </div>

          {/* Mux por provincia / ciudad AR — A/B conversión */}
          <div className="space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium">Mux por provincia / ciudad (AR)</p>
                <p className="text-xs text-muted-foreground">
                  Prioridad: <strong>ciudad</strong> → provincia → país AR → global.
                  Requiere plantilla Mux en AR (Dual / Invitacion) para medir conversión
                  por geo en PostHog (<code className="text-[10px]">playback_id_source</code>
                  = ar_city / ar_province).
                </p>
              </div>
              <div className="flex gap-2 text-xs">
                <Badge variant="secondary">{arGeoCounts.provinces} prov.</Badge>
                <Badge variant="secondary">{arGeoCounts.cities} ciudades</Badge>
              </div>
            </div>
            <Tabs
              value={arGeoTab}
              onValueChange={(v) => setArGeoTab(v as "provinces" | "cities")}
            >
              <TabsList className="h-9">
                <TabsTrigger value="provinces" className="text-xs">
                  Provincias
                </TabsTrigger>
                <TabsTrigger value="cities" className="text-xs">
                  Ciudades
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {arGeoTab === "provinces" ? (
              <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                {AR_PROVINCES.map((p) => (
                  <div
                    key={p.code}
                    className="grid gap-1.5 rounded-md border bg-background/60 p-2 sm:grid-cols-[140px_1fr] sm:items-center"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium">{p.name}</p>
                      <p className="text-[10px] text-muted-foreground">{p.region}</p>
                    </div>
                    <MuxVideoPicker
                      value={muxByArGeo.provinces[p.code] ?? ""}
                      onChange={(id) => setArProvinceMux(p.code, id)}
                      placeholder="Vacío = heredar país AR / global"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  value={arCityFilter}
                  onChange={(e) => setArCityFilter(e.target.value)}
                  placeholder="Filtrar ciudad (Palermo, Rosario…)"
                  className="h-9"
                />
                <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                  {filteredArCities.map((c) => (
                    <div
                      key={c.geo_code}
                      className="grid gap-1.5 rounded-md border bg-background/60 p-2 sm:grid-cols-[160px_1fr] sm:items-center"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium">{c.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {c.provinceCode} · {c.geo_code}
                        </p>
                      </div>
                      <MuxVideoPicker
                        value={muxByArGeo.cities[c.geo_code] ?? ""}
                        onChange={(id) => setArCityMux(c.geo_code, id)}
                        placeholder="Vacío = heredar provincia / AR"
                      />
                    </div>
                  ))}
                  {filteredArCities.length === 0 && (
                    <p className="text-xs text-muted-foreground">Sin ciudades para ese filtro.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Plantilla global — cards */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4" />
            Plantilla global
          </CardTitle>
          <CardDescription>
            Países sin override específico verán esta plantilla en <code>/</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {TEMPLATE_CARDS.map((card) => {
              const selected = globalTpl === card.value;
              return (
                <button
                  key={card.value}
                  type="button"
                  onClick={() => setGlobalTpl(card.value)}
                  className={cn(
                    "rounded-xl border p-3 text-left transition-all hover:border-foreground/20",
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "bg-card/50",
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold">{card.title}</p>
                      <p className="text-[11px] text-muted-foreground">{card.subtitle}</p>
                      {card.videoOnInit ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
                          <PlayCircle className="h-3 w-3" />
                          Video al abrir
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">Requiere clic</span>
                      )}
                    </div>
                    {selected && <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />}
                    {card.recommended && !selected && (
                      <Badge className="shrink-0 text-[9px]">Top</Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={globalTpl} onValueChange={setGlobalTpl}>
              <SelectTrigger className="w-full max-w-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERNATIONAL_HOME_TEMPLATE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => setGlobalTpl(INVITACION_EXCLUSIVA_MUX_TEMPLATE)}>
              <Video className="mr-1 h-3 w-3" />
              Mux nativo
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setGlobalTpl(DUAL_CTA_VIDEO_LANDING_TEMPLATE)}
            >
              <Layers className="mr-1 h-3 w-3" />
              Dual CTA
            </Button>
          </div>

          {isInvitacionExclusivaMuxTemplate(globalTpl) && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              <Radio className="mr-1 inline h-3 w-3" />
              MuxVideoPlayerWithCTA nativo: video fullscreen + CTA membresía Stripe.
            </p>
          )}

          {(isMuxVideoPlayerHomeTemplate(globalTpl) ||
            isDualCtaVideoLandingTemplate(globalTpl)) && (
            <div className="space-y-3 rounded-lg border border-sky-500/20 bg-sky-500/[0.04] p-3">
              <p className="text-[11px] font-medium text-sky-700 dark:text-sky-400">
                Precio VIP Stripe global — bulk (Dual CTA + Mux nativo)
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground">
                    Stripe Price ID (bulk)
                  </label>
                  <Input
                    type="text"
                    placeholder="price_1TsRwOH8xCanmtKXBEW8SAea"
                    value={globalPricingDraft.stripePriceId}
                    onChange={(e) =>
                      setGlobalPricingDraft((p) => ({
                        ...p,
                        stripePriceId: e.target.value,
                      }))
                    }
                    className="h-9 font-mono text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground">
                    Stripe Product ID (esta brand)
                  </label>
                  <Input
                    type="text"
                    readOnly
                    title="Un solo product VIP para anaafernandez"
                    value={
                      globalPricingDraft.stripeProductId || "prod_UsCLwRCliyEnz0"
                    }
                    className="h-9 font-mono text-sm opacity-80"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground">
                    Precio display USD (bulk)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="29.99"
                    value={globalPricingDraft.fullPriceUsd}
                    onChange={(e) =>
                      setGlobalPricingDraft((p) => ({
                        ...p,
                        fullPriceUsd: e.target.value,
                      }))
                    }
                    className="h-9 font-mono text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground">
                    % off 1.er mes (copy UI)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="90"
                    step="1"
                    placeholder="70"
                    value={globalPricingDraft.firstMonthDiscountPct}
                    onChange={(e) =>
                      setGlobalPricingDraft((p) => ({
                        ...p,
                        firstMonthDiscountPct: e.target.value,
                      }))
                    }
                    className="h-9 font-mono text-sm"
                    disabled={globalPricingDraft.applyDiscount !== "true"}
                  />
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={globalPricingDraft.applyDiscount === "true"}
                  onChange={(e) =>
                    setGlobalPricingDraft((p) =>
                      e.target.checked
                        ? { ...p, applyDiscount: "true" }
                        : {
                            ...p,
                            applyDiscount: "",
                            firstMonthDiscountPct: "",
                            promotionCodeId: "",
                          },
                    )
                  }
                  className="h-3.5 w-3.5 rounded border-white/30"
                />
                Aplicar promo 1.er mes en bulk
              </label>
              {globalPricingDraft.applyDiscount === "true" && (
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground">
                    Promotion Code ID (promo_…)
                  </label>
                  <Input
                    type="text"
                    placeholder="promo_1TzyMyH8xCanmtKXKsOmWgAp"
                    value={globalPricingDraft.promotionCodeId}
                    onChange={(e) =>
                      setGlobalPricingDraft((p) => ({
                        ...p,
                        promotionCodeId: e.target.value,
                      }))
                    }
                    className="h-9 font-mono text-sm"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-[10px] text-muted-foreground">
                  Canal gratis Telegram
                </label>
                <Select
                  value={globalDualCtaFreeTelegram}
                  onValueChange={(v) =>
                    setGlobalDualCtaFreeTelegram(v as FreeTelegramTriState)
                  }
                >
                  <SelectTrigger className="h-9 max-w-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">
                      Heredar premium-invite-config
                    </SelectItem>
                    <SelectItem value="on">Mostrar (si hay URL)</SelectItem>
                    <SelectItem value="off">Ocultar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  disabled={publishing}
                  onClick={() => void applyGlobalPricingToAllAndPublish()}
                >
                  {publishing ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Publicando…
                    </>
                  ) : (
                    <>
                      <UploadCloud className="mr-1.5 h-3.5 w-3.5" />
                      Aplicar VIP a {allIntlCountryCodes.length} países y publicar live
                    </>
                  )}
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={applyGlobalPricingToAllDualCta}>
                  Solo draft (sin publicar)
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={applyGlobalFreeTelegramToAllDualCta}>
                  Propagar Telegram a todos
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={applyDualCtaToAllIntl}>
                  Dual CTA en todos (sin AR)
                </Button>
              </div>
              <p className="text-[10px] leading-snug text-muted-foreground">
                Por defecto:{" "}
                <code className="text-[9px]">{FALLBACK_VIP_STRIPE_PRICE_ID}</code> + ANA70{" "}
                <code className="text-[9px]">{FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID}</code> (−
                {FALLBACK_VIP_DISPLAY_DISCOUNT_PCT}%) en <strong>todos</strong> los países
                no-AR. Cada país puede overridear abajo. Aunque el mapa esté vacío, el
                front ya usa ese fallback canónico.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Video global: control principal arriba (hero DualCTA). Aquí solo atajo. */}
      <p className="text-center text-[11px] text-muted-foreground">
        Video DualCTA al init → card superior. Avanzado:{" "}
        <Link
          to="/admin/premium-invite-config"
          className="underline underline-offset-2"
        >
          Premium Invite
        </Link>
        .
      </p>

      {/* Lista de países */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Todos los países ({COUNTRIES.length})</CardTitle>
          <CardDescription>
            Plantilla, Mux, precio VIP y Telegram por ISO2. Buscá o elegí un país
            para editar al instante.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1">
              <label className="text-[11px] font-medium text-muted-foreground">
                Ir a país
              </label>
              <Select
                value={quickPickCountry || undefined}
                onValueChange={(code) => {
                  setQuickPickCountry(code);
                  jumpToCountry(code);
                }}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Elegí cualquier país…" />
                </SelectTrigger>
                <SelectContent className="max-h-[min(360px,70dvh)]">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {flag(c.code)} {c.name} ({c.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-10 shrink-0"
              onClick={() => {
                if (quickPickCountry) jumpToCountry(quickPickCountry);
              }}
              disabled={!quickPickCountry}
            >
              Editar
            </Button>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Tabs
              value={listTab}
              onValueChange={(v) => {
                setListTab(v as ListFilter);
                setAllVisibleLimit(48);
              }}
            >
              <TabsList className="h-auto w-full justify-start overflow-x-auto sm:w-auto">
                <TabsTrigger value="priority" className="min-h-[40px] shrink-0">
                  Prioritarios
                </TabsTrigger>
                <TabsTrigger value="overrides" className="min-h-[40px] shrink-0">
                  Con override{overrideCount > 0 ? ` (${overrideCount})` : ""}
                </TabsTrigger>
                <TabsTrigger value="mux" className="min-h-[40px] shrink-0">
                  Mux propio{muxOverrideCount > 0 ? ` (${muxOverrideCount})` : ""}
                </TabsTrigger>
                <TabsTrigger value="all" className="min-h-[40px] shrink-0">
                  Todos ({COUNTRIES.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar país…"
              className="max-w-xs"
            />
          </div>

          {(overrideCount > 0 || muxOverrideCount > 0) && (
            <Button variant="ghost" size="sm" onClick={resetAllOverrides} className="text-xs">
              <RotateCcw className="mr-1 h-3 w-3" />
              Limpiar todos los overrides
            </Button>
          )}

          <div className="space-y-2">
            {visibleCountries.map((c) => (
              <CountryTemplateRow
                key={c.code}
                code={c.code}
                name={c.name}
                compact={listTab === "all"}
                {...rowProps}
              />
            ))}
          </div>

          {visibleCountries.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {listTab === "overrides"
                ? "Ningún país con override todavía — usá Prioritarios o Todos."
                : listTab === "mux"
                  ? "Ningún playback Mux por país configurado."
                  : `Sin resultados para "${filter}".`}
            </p>
          )}

          {listTab === "all" &&
            !filter.trim() &&
            filteredCountries.length > allVisibleLimit && (
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAllVisibleLimit((n) => n + 60)}
              >
                Cargar más (+60)
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAllVisibleLimit(COUNTRIES.length)}
              >
                Mostrar los {COUNTRIES.length} países
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {editingCountry && editingCountryMeta && isMobile && (
          <Sheet
            open
            onOpenChange={(open) => {
              if (!open) setEditingCountry(null);
            }}
          >
            <SheetContent
              side="bottom"
              className="max-h-[92dvh] overflow-y-auto rounded-t-2xl px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-6"
            >
              <SheetHeader className="text-left">
                <SheetTitle className="flex items-center gap-2 text-base">
                  <span className="text-xl">{flag(editingCountry)}</span>
                  {editingCountryMeta.name}
                  <span className="font-mono text-xs text-muted-foreground">
                    {editingCountry}
                  </span>
                </SheetTitle>
                <SheetDescription>
                  Plantilla, Mux, precio VIP y Telegram para{" "}
                  <code>{editingCountry}</code>. Preview = GeoSim edge (no solo{" "}
                  <code>?country=</code>).
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5">
                <CountryEditorPanel
                  code={editingCountry}
                  globalTpl={globalTpl}
                  byCountry={byCountry}
                  muxByCountry={muxByCountry}
                  pricingByCountry={pricingByCountry}
                  freeTelegramByCountry={freeTelegramByCountry}
                  onSetCountry={setCountry}
                  onSetMuxVariants={setMuxVariantsWithTemplateGuard}
                  onSetPricing={setPricing}
                  onCreateStripeVip={createStripeVipForCountry}
                  creatingStripeFor={creatingStripeFor}
                  onSetFreeTelegram={setFreeTelegram}
                  onApplyMux={applyMuxNativeToCountry}
                  onApplyDualCta={applyDualCtaToCountry}
                  stacked
                />
              </div>
              <div className="mt-4 flex gap-2">
                <GeoSimCountryPreviewButton
                  country={editingCountry}
                  source="country_templates_sheet"
                  className="h-11 flex-1"
                  label="Preview GeoSim"
                  title={`GeoSim edge · ${editingCountry}`}
                />
                <Button className="h-11 flex-1" onClick={() => setEditingCountry(null)}>
                  Listo
                </Button>
              </div>
            </SheetContent>
          </Sheet>
      )}

      {editingCountry && editingCountryMeta && !isMobile && (
          <Dialog
            open
            onOpenChange={(open) => {
              if (!open) setEditingCountry(null);
            }}
          >
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base">
                  <span className="text-xl">{flag(editingCountry)}</span>
                  {editingCountryMeta.name}
                  <span className="font-mono text-xs text-muted-foreground">
                    {editingCountry}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  Configuración para geo <code>{editingCountry}</code> en la home.
                </DialogDescription>
              </DialogHeader>
              <CountryEditorPanel
                code={editingCountry}
                globalTpl={globalTpl}
                byCountry={byCountry}
                muxByCountry={muxByCountry}
                pricingByCountry={pricingByCountry}
                freeTelegramByCountry={freeTelegramByCountry}
                onSetCountry={setCountry}
                onSetMuxVariants={setMuxVariantsWithTemplateGuard}
                onSetPricing={setPricing}
                onCreateStripeVip={createStripeVipForCountry}
                creatingStripeFor={creatingStripeFor}
                onSetFreeTelegram={setFreeTelegram}
                onApplyMux={applyMuxNativeToCountry}
                onApplyDualCta={applyDualCtaToCountry}
                stacked
              />
              <div className="flex gap-2 pt-2">
                <GeoSimCountryPreviewButton
                  country={editingCountry}
                  source="country_templates_dialog"
                  size="sm"
                  label="Preview GeoSim"
                  title={`GeoSim edge · ${editingCountry}`}
                />
                <Button size="sm" onClick={() => setEditingCountry(null)}>
                  Listo
                </Button>
              </div>
            </DialogContent>
          </Dialog>
      )}

      {/* Sticky actions — siempre fijo en móvil */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80",
          "max-md:shadow-[0_-8px_24px_rgba(0,0,0,0.08)]",
          !hasUnsavedChanges &&
            !hasUnpublishedOnServer &&
            !invalidMuxEntries.length &&
            "md:static md:border-0 md:bg-transparent md:px-0 md:py-0 md:shadow-none md:backdrop-blur-none",
        )}
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2">
          <Button
            className="h-11 min-h-[44px] flex-1 sm:flex-none"
            onClick={saveAndPublish}
            disabled={publishing || savingDraft || invalidMuxEntries.length > 0}
          >
            {publishing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {publishing ? "Guardando live…" : "Guardar (live al instante)"}
          </Button>
          <Button
            variant="outline"
            className="h-11 min-h-[44px] flex-1 sm:flex-none"
            onClick={saveDraft}
            disabled={savingDraft || publishing || invalidMuxEntries.length > 0}
          >
            {savingDraft ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UploadCloud className="mr-2 h-4 w-4" />
            )}
            Solo borrador
          </Button>
          {hasUnsavedChanges && (
            <Button variant="ghost" size="sm" onClick={handleDiscard}>
              Descartar
            </Button>
          )}
          {invalidMuxEntries.length > 0 && (
            <span className="text-xs text-destructive">
              {invalidMuxEntries.length} playback ID inválido(s) — corregí antes de guardar
            </span>
          )}
          <p className="ml-auto hidden max-w-xs text-xs text-muted-foreground lg:block">
            Guardar = MuxVideoPlayerWithCTA por país en prod (sin deploy). Borrador = preview{" "}
            <code className="text-[10px]">?site_settings_variant=test</code>.
          </p>
        </div>
      </div>
    </AdminPageShell>
  );
}